import React, { useState, useEffect, useCallback, useRef } from 'react';
import { motion } from 'framer-motion';
import {
  DndContext,
  closestCenter,
  KeyboardSensor,
  PointerSensor,
  useSensor,
  useSensors,
} from '@dnd-kit/core';
import {
  arrayMove,
  SortableContext,
  sortableKeyboardCoordinates,
  verticalListSortingStrategy,
} from '@dnd-kit/sortable';
import {
  useSortable,
} from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import { 
  X, 
  Calculator, 
  FileText,
  Upload,
  Save,
  Plus,
  Edit,
  Trash2,
  Settings,
  Database,
  AlertCircle,
  Eye,
  Download,
  GripVertical,
  GitMerge,
  CheckSquare,
  Square,
  Search,
  Lock,
  Unlock,
  Video,
  Languages
} from 'lucide-react';
import { QuestionComponent } from '../../components/QuestionTypes';
import KatexRenderer from '../../components/KatexRenderer';
import MarkingSchemeDisplay from '../../components/MarkingSchemeDisplay';
import DynamicSectionConfig from '../../components/DynamicSectionConfig';
import EnhancedTextEditor from '../../components/EnhancedTextEditor';
import RichTextEditorWithImages from '../../components/RichTextEditorWithImages';
import TestEvaluationModal from '../../components/admin/TestEvaluationModal';
import { 
  saveTest, 
  getTests, 
  updateTest, 
  deleteTest, 
  validateTestData, 
  sanitizeTestData,
  bulkSaveTests,
  subscribeToTests,
  getTestResultsByTestId,
  COLLECTIONS
} from '../../firebase/collections';
import { parseQuestionWithMathpix, parseQuestionWithMathpixFromDataUrl } from '../../services/mathpix';
import { doc, getDoc, setDoc, updateDoc } from 'firebase/firestore';
import { db } from '../../firebase/config';
import { 
  uploadFile, 
  deleteFile, 
  generateFilePath, 
  validateFile, 
  formatFileSize,
  batchUploadFiles
} from '../../firebase/r2storage';
import { useExam } from '../../contexts/ExamContext';
import { useRealtime } from '../../contexts/RealtimeContext';
import { isCoachingAdmin as checkIsCoachingAdmin } from '../../utils/rbac';
import RegexQuestionParser from '../../utils/regexQuestionParser';
import { jeemains2025shift1Data, addJEEMains2025Shift1Test } from '../../utils/jeemains2025shift1';

// Sortable Preview Question Item Component for Merge Modal
const SortablePreviewQuestionItem = ({ question, index, markingScheme }) => {
  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging,
  } = useSortable({ id: question.id });

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.5 : 1,
  };

  return (
    <div
      ref={setNodeRef}
      style={style}
      className={`bg-white dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700 p-4 ${
        isDragging ? 'shadow-lg z-10' : ''
      }`}
    >
      <div className="flex items-start gap-3">
        <div
          {...attributes}
          {...listeners}
          className="cursor-grab active:cursor-grabbing p-2 text-gray-400 hover:text-gray-600 hover:bg-gray-100 dark:hover:bg-gray-700 rounded mt-1"
          title="Drag to reorder"
        >
          <GripVertical size={18} />
        </div>
        
        <div className="flex-1">
          <div className="flex items-center gap-2 mb-2">
            <span className="bg-blue-100 dark:bg-blue-900/30 text-blue-800 dark:text-blue-300 text-xs font-semibold px-2 py-1 rounded">
              Q{index + 1}
            </span>
            <span className="bg-gray-100 dark:bg-gray-700 text-gray-700 dark:text-gray-300 text-xs px-2 py-1 rounded">
              {question.type || 'multiple-choice'}
            </span>
            {question.difficulty && (
              <span className={`text-xs px-2 py-1 rounded ${
                question.difficulty === 'Easy' ? 'bg-green-100 text-green-800 dark:bg-green-900/30 dark:text-green-300' :
                question.difficulty === 'Medium' ? 'bg-yellow-100 text-yellow-800 dark:bg-yellow-900/30 dark:text-yellow-300' :
                'bg-red-100 text-red-800 dark:bg-red-900/30 dark:text-red-300'
              }`}>
                {question.difficulty}
              </span>
            )}
            {question.hasDiagram && (
              <span className="bg-purple-100 dark:bg-purple-900/30 text-purple-800 dark:text-purple-300 text-xs px-2 py-1 rounded">
                Diagram
              </span>
            )}
          </div>
          
          <div className="text-sm text-gray-700 dark:text-gray-300 mb-2">
            <KatexRenderer content={question.question || 'No question text'} />
          </div>
          
          {question.questionImageUrl && (
            <div className="mb-2">
              <img
                src={question.questionImageUrl}
                alt="Question"
                className="max-w-full h-auto max-h-32 rounded"
              />
            </div>
          )}
          
          {/* Show options for multiple choice */}
          {(question.type === 'multiple-choice' || question.type === 'multiple-choice-multi') && question.options && question.options.length > 0 && (
            <div className="mt-2 space-y-1">
              {question.options.slice(0, 2).map((option, optIndex) => (
                <div key={optIndex} className="text-xs text-gray-600 dark:text-gray-400 flex items-start">
                  <span className="mr-2">{String.fromCharCode(65 + optIndex)}.</span>
                  <span className="line-clamp-1">
                    <KatexRenderer content={option} />
                  </span>
                </div>
              ))}
              {question.options.length > 2 && (
                <div className="text-xs text-gray-500 dark:text-gray-500">
                  +{question.options.length - 2} more options
                </div>
              )}
            </div>
          )}
          
          <div className="mt-2 flex items-center gap-2 text-xs text-gray-500 dark:text-gray-400">
            <MarkingSchemeDisplay
              markingScheme={question.markingScheme || markingScheme}
              questionType={question.type}
            />
          </div>
        </div>
      </div>
    </div>
  );
};

// Sortable Question Item Component
const SortableQuestionItem = ({ question, index, onEdit, onRemove }) => {
  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging,
  } = useSortable({ id: question.id });

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.5 : 1,
  };

  return (
    <div
      ref={setNodeRef}
      style={style}
      className={`border border-gray-200 rounded-lg p-4 ${isDragging ? 'shadow-lg' : ''}`}
    >
      <div className="flex justify-between items-start mb-2">
        <div className="flex items-center space-x-2">
          <div
            {...attributes}
            {...listeners}
            className="cursor-grab hover:cursor-grabbing p-1 text-gray-400 hover:text-gray-600 hover:bg-gray-100 rounded"
            title="Drag to reorder questions"
          >
            <GripVertical size={16} />
          </div>
          <span className="bg-blue-100 text-blue-800 text-xs px-2 py-1 rounded">
            Q{index + 1}
          </span>
          <span className={`text-xs px-2 py-1 rounded ${
            question.type === 'rc' ? 'bg-green-100 text-green-800' :
            question.type === 'lrdi-set' ? 'bg-purple-100 text-purple-800' :
            'bg-gray-100 text-gray-800'
          }`}>
            {question.type === 'rc' ? '📖 RC' : 
             question.type === 'lrdi-set' ? '📊 LRDI' : 
             question.type}
          </span>
          {(question.type === 'rc' || question.type === 'lrdi-set') && (question.passageGroupId || question.setGroupId) && (
            <span className="bg-blue-100 text-blue-800 text-xs px-2 py-1 rounded">
              Group: {question.passageGroupId || question.setGroupId}
            </span>
          )}
          {question.difficulty && (
            <span className={`text-xs px-2 py-1 rounded ${
              question.difficulty === 'Easy' ? 'bg-green-100 text-green-800' :
              question.difficulty === 'Medium' ? 'bg-yellow-100 text-yellow-800' :
              'bg-red-100 text-red-800'
            }`}>
              {question.difficulty}
            </span>
          )}
          {question.hasDiagram && (
            <span className="bg-purple-100 text-purple-800 text-xs px-2 py-1 rounded flex items-center">
              <svg className="w-3 h-3 mr-1" fill="currentColor" viewBox="0 0 20 20">
                <path fillRule="evenodd" d="M4 3a2 2 0 00-2 2v10a2 2 0 002 2h12a2 2 0 002-2V5a2 2 0 00-2-2H4zm12 12H4l4-8 3 6 2-4 3 6z" clipRule="evenodd" />
              </svg>
              Diagram
            </span>
          )}
        </div>
        <div className="flex space-x-2">
          <button
            onClick={() => onEdit(index)}
            className="text-blue-600 hover:text-blue-800 hover:bg-blue-50 dark:hover:bg-blue-900/20 p-1.5 rounded transition-colors"
            title="Edit question"
          >
            <Edit size={16} />
          </button>
          <button
            onClick={() => onRemove(index)}
            className="text-red-600 hover:text-red-800 hover:bg-red-50 dark:hover:bg-red-900/20 p-1.5 rounded transition-colors"
            title="Delete question"
          >
            <Trash2 size={16} />
          </button>
        </div>
      </div>
      
      <div className="mb-2">
        <KatexRenderer content={question.question || ''} />
        {/* Legacy support: show separate question image if exists and not already in HTML */}
        {question.questionImageUrl && !question.question?.includes('<img') && (
          <div className="mt-2">
            <img
              src={question.questionImageUrl}
              alt="Question"
              className="max-w-full h-auto max-h-48 rounded"
            />
          </div>
        )}
        
        {/* Display options with images for multiple choice questions */}
        {(question.type === 'multiple-choice' || question.type === 'multiple-choice-multi') && question.options && (
          <div className="mt-3 space-y-2">
            {question.options.map((option, optIndex) => (
              <div key={optIndex} className="flex items-start space-x-2 p-2 bg-gray-50 rounded">
                <div className="flex items-center mt-1">
                  <input
                    type={question.type === 'multiple-choice-multi' ? "checkbox" : "radio"}
                    checked={question.type === 'multiple-choice-multi' 
                      ? Array.isArray(question.correctAnswer) && question.correctAnswer.includes(optIndex)
                      : question.correctAnswer === optIndex
                    }
                    readOnly
                    className="mr-2"
                  />
                </div>
                <div className="flex-1">
                  <div className="text-sm">
                    <KatexRenderer content={option || ''} />
                  </div>
                  {/* Legacy support: show separate option image if exists and not already in HTML */}
                  {question.optionImageUrls && question.optionImageUrls[optIndex] && !option?.includes('<img') && (
                    <div className="mt-1">
                      <img
                        src={question.optionImageUrls[optIndex]}
                        alt={`Option ${optIndex + 1}`}
                        className="max-w-full h-auto max-h-24 rounded"
                      />
                    </div>
                  )}
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
      
      <MarkingSchemeDisplay
        markingScheme={question.markingScheme}
        questionType={question.type}
      />
    </div>
  );
};

const TestManagement = () => {
  const { currentUser, userProfile } = useRealtime();
  
  // Check if user is a super admin (not coaching admin)
  const isSuperAdmin = userProfile?.role === 'admin' || 
                       userProfile?.role === 'superadmin' || 
                       userProfile?.role === 'founder';
  const isCoachingAdmin = checkIsCoachingAdmin(userProfile, currentUser);
  
  // Only use coachingId if user is a coaching admin or staff, not a super admin
  const coachingId = isSuperAdmin ? null : (userProfile?.coachingId || null);
  
  const [tests, setTests] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [showCreateForm, setShowCreateForm] = useState(false);
  const [editingTest, setEditingTest] = useState(null);
  const [editingTestId, setEditingTestId] = useState('');
  const [currentStep, setCurrentStep] = useState(1);
  const [searchTerm, setSearchTerm] = useState('');
  const [filterExam, setFilterExam] = useState('');
  const [filterStatus, setFilterStatus] = useState('');
  const [showBulkImport, setShowBulkImport] = useState(false);
  const [bulkData, setBulkData] = useState('');
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(null);
  const [showSectionConfig, setShowSectionConfig] = useState(false);
  const [isSectionConfigValid, setIsSectionConfigValid] = useState(false);
  const [editingQuestionIndex, setEditingQuestionIndex] = useState(null);
  const [showMergeModal, setShowMergeModal] = useState(false);
  const [selectedTests, setSelectedTests] = useState([]);
  const [selectedQuestions, setSelectedQuestions] = useState(new Set());
  const [orderedPreviewQuestions, setOrderedPreviewQuestions] = useState([]);
  const [showPasswordPrompt, setShowPasswordPrompt] = useState(false);
  const [mergePassword, setMergePassword] = useState('');
  const [isMergeUnlocked, setIsMergeUnlocked] = useState(false);
  const [showTranslationModal, setShowTranslationModal] = useState(false);
  const [translatedQuestion, setTranslatedQuestion] = useState(null);
  const [translating, setTranslating] = useState(false);
  const [evaluationModalTest, setEvaluationModalTest] = useState(null); // Test for which evaluation modal is open
  
  
  // Password for merge test section (in production, this should be stored securely)
  const MERGE_TEST_PASSWORD = 'admin123'; // Change this to your desired password
  
  // Update preview questions when selected questions change
  useEffect(() => {
    if (selectedQuestions.size === 0) {
      setOrderedPreviewQuestions([]);
      return;
    }

    // Collect selected questions in order
    const questions = [];
    selectedTests.forEach(testId => {
      const test = tests.find(t => t.id === testId);
      if (test && test.questions) {
        test.questions.forEach((question, qIndex) => {
          const questionId = `${testId}-${qIndex}`;
          if (selectedQuestions.has(questionId)) {
            questions.push({
              ...question,
              mergeQuestionId: questionId, // Keep track of original ID
              id: question.id || `q-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`
            });
          }
        });
      }
    });

    // If we have existing ordered questions, preserve the order for questions that are still selected
    if (orderedPreviewQuestions.length > 0) {
      const existingOrder = orderedPreviewQuestions.map(q => q.mergeQuestionId);
      const newQuestionsMap = new Map(questions.map(q => [q.mergeQuestionId, q]));
      
      // Preserve existing order for questions still selected
      const preserved = existingOrder
        .filter(id => newQuestionsMap.has(id))
        .map(id => newQuestionsMap.get(id));
      
      // Add new questions that weren't in the previous order
      const newIds = new Set(preserved.map(q => q.mergeQuestionId));
      const newQuestions = questions.filter(q => !newIds.has(q.mergeQuestionId));
      
      setOrderedPreviewQuestions([...preserved, ...newQuestions]);
    } else {
      setOrderedPreviewQuestions(questions);
    }
  }, [selectedQuestions, selectedTests, tests]);

  const [mergeTestData, setMergeTestData] = useState({
    id: '',
    title: '',
    description: '',
    duration: '',
    exam: 'CAT',
    difficulty: 'Medium',
    status: 'draft',
    subject: 'General',
    markingScheme: {
      correct: 4,
      incorrect: -1,
      partial: 0
    },
    useDynamicSections: false,
    sections: []
  });

  // Drag and drop sensors
  const sensors = useSensors(
    useSensor(PointerSensor),
    useSensor(KeyboardSensor, {
      coordinateGetter: sortableKeyboardCoordinates,
    })
  );
  const [testData, setTestData] = useState({
    id: '',
    title: '',
    description: '',
    duration: '',
    totalQuestions: '',
    exam: 'CAT',
    difficulty: 'Medium',
    status: 'published',
    subject: 'General', // Add subject field
    questions: [],
    sections: [], // Dynamic sections configuration
    useDynamicSections: false, // Toggle for dynamic sections
    markingScheme: { // Add markingScheme to initial state
      correct: 4,
      incorrect: -1,
      partial: 0
    },
    analysisVideoUrl: '', // URL for 99 percentiler analysis video
    analysisVideoFile: null, // File object for video upload
    enableLanguageSwitch: false, // Enable language switching in test interface
    enableCalculator: false, // Enable on-screen calculator in test interface
    enableQuestionPaper: false, // Enable view question paper button in test interface
    manualEvaluation: false, // Enable manual evaluation mode (for subjective papers)
    isLiveTest: false, // Enable live test mode (fixed start/end time)
    startTime: null, // Start time for live test (ISO string)
    endTime: null, // End time for live test (calculated from startTime + duration)
    interfaceType: 'jee' // Test interface type: 'jee' or 'cat'
  });

  const [currentQuestion, setCurrentQuestion] = useState({
    id: '',
    type: 'multiple-choice',
    question: '',
    questionImage: null,
    questionImageUrl: '',
    latex: '',
    diagramImageUrl: '',
    mathpixAnswerText: '',
    mathpixMarkdown: '',
    options: ['', '', '', ''],
    optionImages: [null, null, null, null],
    optionImageUrls: ['', '', '', ''],
    correctAnswer: 0,
    explanation: '',
    difficulty: 'Medium',
    topic: '',
    allowMultiple: false,
    passage: '', // For RC questions
    setContent: '', // For LRDI Set questions
    passageGroupId: '', // For grouping RC questions
    setGroupId: '', // For grouping LRDI Set questions
    questionNumberInGroup: 1, // Question number within the RC/LRDI group
    markingScheme: {
      correct: 4,
      incorrect: -1,
      partial: 0
    }
  });

  // Question types - add RC and LRDI Set for CAT exams
  const questionTypes = React.useMemo(() => {
    const baseTypes = [
      { value: 'multiple-choice', label: 'Multiple Choice (Single)' },
      { value: 'multiple-choice-multi', label: 'Multiple Choice (Multi-select)' },
      { value: 'integer', label: 'Integer Answer' },
      { value: 'floating-point', label: 'Floating Point Answer' },
      { value: 'subjective', label: 'Subjective/Essay' },
      { value: 'matrix-match', label: 'Matrix Match' },
      { value: 'assertion-reason', label: 'Assertion-Reason' }
    ];
    
    // Add CAT-specific question types
    if (testData.exam === 'CAT' || testData.exam === 'cat') {
      return [
        ...baseTypes,
        { value: 'rc', label: 'Reading Comprehension (RC)' },
        { value: 'lrdi-set', label: 'LRDI Set' }
      ];
    }
    
    return baseTypes;
  }, [testData.exam]);

  const mathpixInputRef = useRef(null);
  const [mathpixLoading, setMathpixLoading] = useState(false);
  const [mathpixStatus, setMathpixStatus] = useState('');
  const [mathpixPdfLoading, setMathpixPdfLoading] = useState(false);
  const { availableExams } = useExam();
  const [coachingExamIds, setCoachingExamIds] = useState([]);
  
  // Load coaching's exam IDs to filter the available exams list (only for coaching admins)
  useEffect(() => {
    if (!coachingId || isSuperAdmin) {
      setCoachingExamIds([]);
      return;
    }
    
    const loadCoachingExams = async () => {
      try {
        const { getAllExamData } = await import('../../firebase/collections');
        const result = await getAllExamData(coachingId);
        if (result.success && result.data) {
          const examsArray = Object.keys(result.data);
          setCoachingExamIds(examsArray);
        } else {
          setCoachingExamIds([]);
        }
      } catch (error) {
        setCoachingExamIds([]);
      }
    };
    loadCoachingExams();
  }, [coachingId, isSuperAdmin]);
  
  // Filter availableExams to only show exams that belong to this coaching (only for coaching admins)
  const filteredAvailableExams = React.useMemo(() => {
    if (isSuperAdmin || !coachingId || !availableExams || availableExams.length === 0) {
      return availableExams || []; // Super admins see all exams
    }
    
    if (coachingExamIds.length === 0) {
      return []; // No exams for this coaching yet
    }
    
    // Filter availableExams to only show exams that belong to this coaching
    return availableExams.filter(exam => {
      const examId = (exam.id || exam.name || '').toLowerCase();
      return coachingExamIds.some(coachingExamId => 
        coachingExamId.toLowerCase() === examId
      );
    });
  }, [availableExams, coachingId, coachingExamIds, isSuperAdmin]);
  
  const exams = React.useMemo(() => {
    if (filteredAvailableExams && filteredAvailableExams.length > 0) {
      return filteredAvailableExams.map(e => e.name || e.id?.toUpperCase());
    }
    // Fallback: always provide at least one exam option
    return ['CAT', 'JEE', 'NEET', 'Banking']; // Default exams if none available
  }, [filteredAvailableExams]);
  const difficulties = ['Easy', 'Medium', 'Hard'];
  const statuses = ['draft', 'published', 'archived'];

  useEffect(() => {
    // Load tests for both super admins and coaching admins
    loadTests();
    // Set up real-time listener with optional coachingId filter
    const filters = coachingId ? { coachingId: coachingId } : {};
    const unsubscribe = subscribeToTests((result) => {
      if (result.success) {
        let testsData = result.data || [];
        
        // For super admins: filter out institute tests (tests with coachingId)
        if (isSuperAdmin) {
          testsData = testsData.filter(test => !test.coachingId);
        }
        
        // Tests are already filtered by coachingId in the query (if provided)
        setTests(testsData);
        // Clear any previous errors on successful update
        setError(null);
      } else {
        // Only set error if there's an actual error, not just empty results
        if (result.error) {
          setError('Failed to load tests');
        }
      }
    }, filters); // Pass filters (with or without coachingId)

    return () => {
      if (unsubscribe) unsubscribe();
    };
  }, [coachingId, isCoachingAdmin, isSuperAdmin]);

  // Sync question editor marking scheme with test-level default marking scheme
  useEffect(() => {
    // Only update if we're not editing an existing question and we have a test-level marking scheme
    // This ensures new questions get the updated default marking scheme when user changes it
    if (editingQuestionIndex === null && testData?.markingScheme) {
      const defaultMarkingScheme = testData.markingScheme;
      
      setCurrentQuestion(prev => {
        // Always sync if this is a new/empty question (no id or no content)
        const isNewQuestion = !prev.id || (!prev.question.trim() && !prev.questionImage && !prev.questionImageUrl);
        
        if (isNewQuestion) {
          return {
            ...prev,
            markingScheme: { ...defaultMarkingScheme }
          };
        }
        
        // For questions with content, always sync with test-level default
        // This ensures that when user changes the test-level default marking scheme,
        // the question editor reflects it immediately
        // User can still customize individual questions, and those customizations will be saved with the question
        return {
          ...prev,
          markingScheme: { ...defaultMarkingScheme }
        };
      });
    }
  }, [testData?.markingScheme?.correct, testData?.markingScheme?.incorrect, testData?.markingScheme?.partial, editingQuestionIndex]);

  const loadTests = async () => {
    try {
      setLoading(true);
      setError(null);
      
      // Pass coachingId only if user is a coaching admin, null for super admins
      const result = await getTests(coachingId || {});
      if (result.success) {
        let testsData = result.data || [];
        
        // For super admins: filter out institute tests (tests with coachingId)
        if (isSuperAdmin) {
          testsData = testsData.filter(test => !test.coachingId);
        }
        
        // Successfully loaded - set tests (empty array is fine, not an error)
        setTests(testsData);
        setError(null); // Clear any previous errors
      } else {
        // Only set error if there's an actual error, not just empty results
        if (result.error) {
          setError(`Failed to load tests: ${result.error}`);
        } else {
          // No error, just no data
          setTests([]);
          setError(null);
        }
      }
    } catch (error) {
      // Only set error for actual exceptions
      setError(`Failed to load tests: ${error.message || error}`);
    } finally {
      setLoading(false);
    }
  };

  const handleCreateTest = () => {
    handleCreateNewTest();
    setCurrentStep(1);
  };

  const handleSectionsChange = (newSections) => {
    setTestData(prev => ({
      ...prev,
      sections: newSections
    }));
  };

  const handleSectionValidationChange = (isValid) => {
    setIsSectionConfigValid(isValid);
  };

  const toggleDynamicSections = () => {
    setTestData(prev => ({
      ...prev,
      useDynamicSections: !prev.useDynamicSections
    }));
  };

  // Helper function to extract and upload images from HTML content
  const processHtmlContentWithImages = async (htmlContent, basePath) => {
    if (!htmlContent || typeof htmlContent !== 'string') {
      return htmlContent;
    }

    const tempDiv = document.createElement('div');
    tempDiv.innerHTML = htmlContent;
    const images = tempDiv.querySelectorAll('img[data-image-id]');
    
    let processedHtml = htmlContent;
    const uploadPromises = [];
    
    for (const img of images) {
      const imageId = img.getAttribute('data-image-id');
      const src = img.src;
      
      // Check if image is a blob URL (needs upload) or already uploaded
      if (src.startsWith('blob:') || src.startsWith('data:')) {
        const uploadPromise = (async () => {
          try {
            // Convert blob/data URL to file
            const response = await fetch(src);
            const blob = await response.blob();
            const fileExtension = blob.type.split('/')[1] || 'png';
            const file = new File([blob], `image_${imageId}.${fileExtension}`, { type: blob.type });
            
            // Upload to Firebase
            const fileName = `${basePath}_${imageId}_${Date.now()}.${fileExtension}`;
            const result = await uploadFile(file, fileName);
            
            if (result.success) {
              // Replace all occurrences of this blob URL with uploaded URL
              // Use a more specific replacement to avoid replacing other images
              const escapedSrc = src.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
              processedHtml = processedHtml.replace(new RegExp(escapedSrc, 'g'), result.url);
              
              // Revoke blob URL to free memory
              if (src.startsWith('blob:')) {
                URL.revokeObjectURL(src);
              }
              
              return { success: true, oldUrl: src, newUrl: result.url };
            }
          } catch (error) {
            return { success: false, error };
          }
        })();
        
        uploadPromises.push(uploadPromise);
      }
    }
    
    // Wait for all uploads to complete
    await Promise.all(uploadPromises);
    
    return processedHtml;
  };

  const handleSaveTest = async () => {
    try {
      setSaving(true);
      // Validate test data
      const validation = validateTestData(testData);
      if (!validation.isValid) {
        setError(`Validation failed: ${validation.errors.join(', ')}`);
        return;
      }

      // Handle image uploads for all questions in the test
      let testDataWithImages = { ...testData };
      
      // Ensure questions array exists
      if (!testData.questions || !Array.isArray(testData.questions)) {
        testData.questions = [];
      }
      
      // Process each question for image uploads
      const processedQuestions = [];
      for (let qIndex = 0; qIndex < testData.questions.length; qIndex++) {
        const question = testData.questions[qIndex];
        let questionData = { ...question };
        
        // Process question HTML content for inline images
        if (question.question && typeof question.question === 'string' && question.question.includes('<img')) {
          try {
            questionData.question = await processHtmlContentWithImages(
              question.question,
              `tests/question_${qIndex}`
            );
          } catch (error) {
          }
        }
        
        // Upload question image if exists (legacy support)
        if (question.questionImage) {
          try {
            const result = await uploadFile(
              question.questionImage, 
              `tests/${Date.now()}_question_${qIndex}_${question.questionImage.name}`
            );
            if (result.success) {
              questionData.questionImageUrl = result.url;
            } else {
              throw new Error(result.error);
            }
          } catch (error) {
            setError(`Failed to upload image for question ${qIndex + 1}`);
            return;
          }
        }

        // Process options HTML content for inline images
        if (question.options && Array.isArray(question.options)) {
          const processedOptions = [];
          for (let i = 0; i < question.options.length; i++) {
            const option = question.options[i];
            if (typeof option === 'string' && option.includes('<img')) {
              try {
                const processedOption = await processHtmlContentWithImages(
                  option,
                  `tests/question_${qIndex}_option_${i}`
                );
                processedOptions.push(processedOption);
              } catch (error) {
                processedOptions.push(option);
              }
            } else {
              processedOptions.push(option);
            }
          }
          questionData.options = processedOptions;
        }

        // Upload option images if exist (legacy support)
        const uploadedOptionUrls = [...(question.optionImageUrls || ['', '', '', ''])];
        for (let i = 0; i < (question.optionImages || []).length; i++) {
          if (question.optionImages[i]) {
            try {
              const result = await uploadFile(
                question.optionImages[i], 
                `tests/${Date.now()}_option_${qIndex}_${i}_${question.optionImages[i].name}`
              );
              if (result.success) {
                uploadedOptionUrls[i] = result.url;
              } else {
                throw new Error(result.error);
              }
            } catch (error) {
              setError(`Failed to upload option ${i + 1} image for question ${qIndex + 1}`);
              return;
            }
          }
        }
        questionData.optionImageUrls = uploadedOptionUrls;

        // Process explanation HTML content for inline images
        if (question.explanation && typeof question.explanation === 'string' && question.explanation.includes('<img')) {
          try {
            questionData.explanation = await processHtmlContentWithImages(
              question.explanation,
              `tests/question_${qIndex}_explanation`
            );
          } catch (error) {
          }
        }

        // Remove file objects before saving to database
        delete questionData.questionImage;
        delete questionData.optionImages;
        
        processedQuestions.push(questionData);
      }
      
      testDataWithImages.questions = processedQuestions;

      // Remove file objects before saving (only keep URLs)
      if (testDataWithImages.analysisVideoFile) {
        delete testDataWithImages.analysisVideoFile;
      }

      // Sanitize test data
      const sanitizedData = sanitizeTestData(testDataWithImages);
      
      // Determine coachingId value - must be string or null, never undefined
      // For coaching admins, use their coachingId; for super admins, use null
      const finalCoachingId = (coachingId && isCoachingAdmin) 
        ? coachingId 
        : null;

      if (editingTest) {
        const targetId = editingTestId || editingTest.id || testData.id;
        if (!targetId) {
          setError('Cannot update: missing test ID. Please enter the test ID in the form above.');
          return;
        }
        
        // Validate that the test ID exists in Firestore before attempting update
        try {
          const testRef = doc(db, COLLECTIONS.TESTS, targetId);
          const testSnap = await getDoc(testRef);
          
          if (!testSnap.exists()) {
            setError(`Test with ID "${targetId}" not found in database. Please check the test ID or create a new test.`);
            return;
          }
        } catch (validationError) {
          setError(`Error validating test ID: ${validationError.message}`);
          return;
        }
        
        // Update existing test - ensure coachingId is included only for coaching admins
        const updateData = {
          ...sanitizedData
        };
        
        // Only include coachingId if it has a valid value (not undefined)
        if (finalCoachingId !== undefined && finalCoachingId !== null) {
          updateData.coachingId = finalCoachingId;
        } else if (isSuperAdmin) {
          // Explicitly set to null for super admins to avoid undefined
          updateData.coachingId = null;
        }
        
        const result = await updateTest(targetId, updateData);
        if (result.success) {
          setSuccess('Test updated successfully');
          setShowCreateForm(false);
          setEditingTest(null);
          setEditingTestId('');
          // Reload to reflect changes immediately in case listener is delayed
          loadTests();
        } else {
          const reason = result.error?.message || result.error || 'Unknown error';
          setError(`Failed to update test: ${reason}`);
        }
      } else {
        // Create new test - use status from form
        const testDataWithStatus = {
          ...sanitizedData,
          status: testData.status || 'published'
        };
        
        // Only include coachingId if it has a valid value (not undefined)
        if (finalCoachingId !== undefined && finalCoachingId !== null) {
          testDataWithStatus.coachingId = finalCoachingId;
        } else if (isSuperAdmin) {
          // Explicitly set to null for super admins to avoid undefined
          testDataWithStatus.coachingId = null;
        }
        
        // If a specific ID is provided, create the test with that ID
        if (testData.id) {
          try {
            // Check if test with this ID already exists
            const testRef = doc(db, COLLECTIONS.TESTS, testData.id);
            const testSnap = await getDoc(testRef);
            
            if (testSnap.exists()) {
              setError(`Test with ID "${testData.id}" already exists. Please use a different ID or edit the existing test.`);
              return;
            }
            
            // Create test with specific ID - ensure coachingId is valid (string or null, never undefined)
            const docData = {
              ...testDataWithStatus,
              createdAt: new Date(),
              updatedAt: new Date()
            };
            
            // Only include coachingId if it has a valid value (not undefined)
            if (finalCoachingId !== undefined && finalCoachingId !== null) {
              docData.coachingId = finalCoachingId;
            } else if (isSuperAdmin) {
              // Explicitly set to null for super admins to avoid undefined
              docData.coachingId = null;
            }
            
            await setDoc(testRef, docData);
            
            const statusMessage = testData.status === 'published' ? 'and published' : 'as draft';
            setSuccess(`Test created successfully ${statusMessage} (ID: ${testData.id})`);
            setShowCreateForm(false);
            loadTests();
          } catch (error) {
            setError(`Failed to create test with specific ID: ${error.message}`);
          }
        } else {
          // Create test with auto-generated ID
          const result = await saveTest(testDataWithStatus);
          if (result.success) {
            const statusMessage = testData.status === 'published' ? 'and published' : 'as draft';
            setSuccess(`Test created successfully ${statusMessage} (ID: ${result.id})`);
            setShowCreateForm(false);
            // Reload tests to show the new test
            loadTests();
          } else {
            setError(`Failed to create test: ${result.error || 'Unknown error'}`);
          }
        }
      }
    } catch (error) {
      const reason = error?.message || 'Unknown error';
      setError(`Failed to save test: ${reason}`);
    } finally {
      setSaving(false);
    }
  };

  const handleEditTest = (test) => {
    setEditingTest(test);
    const testId = test?.id || '';
    setEditingTestId(testId);
    setTestData({
      ...test,
      id: testId, // Ensure ID is set in testData
      questions: test.questions || [], // Ensure questions array exists
      sections: test.sections || [], // Ensure sections array exists
      totalQuestions: test.questions ? test.questions.length : 0,
      useDynamicSections: test.useDynamicSections || false, // Ensure useDynamicSections exists
      subject: test.subject || 'General', // Ensure subject exists
      markingScheme: test.markingScheme || { // Ensure markingScheme exists
        correct: 4,
        incorrect: -1,
        partial: 0
      },
      analysisVideoUrl: test.analysisVideoUrl || '', // Ensure analysisVideoUrl exists
      analysisVideoFile: null, // Reset file on edit
      enableLanguageSwitch: test.enableLanguageSwitch || false, // Ensure enableLanguageSwitch exists
      enableCalculator: test.enableCalculator || false, // Ensure enableCalculator exists
      enableQuestionPaper: test.enableQuestionPaper || false, // Ensure enableQuestionPaper exists
      manualEvaluation: test.manualEvaluation || false, // Ensure manualEvaluation exists
      isLiveTest: test.isLiveTest || false, // Ensure isLiveTest exists
      startTime: test.startTime || null, // Ensure startTime exists
      endTime: test.endTime || null, // Ensure endTime exists
      interfaceType: test.interfaceType || 'jee' // Ensure interfaceType exists, default to 'jee'
    });
    setShowCreateForm(true);
  };

  const handleCreateNewTest = () => {
    setEditingTest(null);
    setEditingTestId('');
    setTestData({
      id: '',
      title: '',
      description: '',
      duration: '',
      totalQuestions: '',
      exam: (exams && Array.isArray(exams) && exams.length > 0) ? exams[0] : 'CAT',
      difficulty: 'Medium',
      status: 'published',
      subject: 'General',
      questions: [],
      sections: [],
      useDynamicSections: false,
      markingScheme: {
        correct: 4,
        incorrect: -1,
        partial: 0
      },
      enableLanguageSwitch: false,
      enableCalculator: false,
      enableQuestionPaper: false,
      manualEvaluation: false,
      isLiveTest: false,
      startTime: null,
      endTime: null
    });
    setShowCreateForm(true);
  };

  const handleDeleteTest = async (testId) => {
    if (window.confirm('Are you sure you want to delete this test?')) {
      try {
        const result = await deleteTest(testId);
        if (result.success) {
          setSuccess('Test deleted successfully');
        } else {
          setError('Failed to delete test');
        }
      } catch (error) {
        setError('Failed to delete test');
        }
    }
  };

  const pdfjsLibRef = useRef(null);
  const pdfjsWorkerSrcRef = useRef(null);

  const loadPdfjsLib = async () => {
    if (pdfjsLibRef.current) return pdfjsLibRef.current;

    let pdfjsLib;
    let workerSrc;

    try {
      pdfjsLib = await import('pdfjs-dist');
      if (pdfjsLib && pdfjsLib.default && !pdfjsLib.getDocument) {
        pdfjsLib = pdfjsLib.default;
      }
    } catch (_) {
      try {
        pdfjsLib = await import('pdfjs-dist/legacy/build/pdf');
        if (pdfjsLib && pdfjsLib.default && !pdfjsLib.getDocument) {
          pdfjsLib = pdfjsLib.default;
        }
      } catch (e2) {
        throw new Error("PDF import couldn't load 'pdfjs-dist'. Ensure it's installed and restart dev server.");
      }
    }

    try {
      const wk = await import('pdfjs-dist/build/pdf.worker.min.js');
      workerSrc = (wk && (wk.default || wk)) || wk;
    } catch (_) {
      try {
        const wkLegacy = await import('pdfjs-dist/legacy/build/pdf.worker');
        workerSrc = (wkLegacy && (wkLegacy.default || wkLegacy)) || wkLegacy;
      } catch (e3) {
        try {
          const wkEntry = await import('pdfjs-dist/build/pdf.worker.entry');
          workerSrc = (wkEntry && (wkEntry.default || wkEntry)) || wkEntry;
        } catch (e4) {
          throw new Error("PDF worker not found. Try reinstalling: npm i pdfjs-dist, then restart the dev server.");
        }
      }
    }

    if (pdfjsLib && pdfjsLib.GlobalWorkerOptions) {
      const resolvedWorkerSrc = typeof workerSrc === 'string'
        ? workerSrc
        : (typeof window !== 'undefined' && typeof window.pdfjsWorkerSrc === 'string'
            ? window.pdfjsWorkerSrc
            : 'https://unpkg.com/pdfjs-dist@4.4.168/build/pdf.worker.min.js');
      pdfjsLib.GlobalWorkerOptions.workerSrc = resolvedWorkerSrc;
      pdfjsWorkerSrcRef.current = resolvedWorkerSrc;
    }

    pdfjsLibRef.current = pdfjsLib;
    return pdfjsLib;
  };

  const handlePublishTest = async (testId) => {
    try {
      const result = await updateTest(testId, { status: 'published' });
      if (result.success) {
        setSuccess('Test published successfully');
      } else {
        setError('Failed to publish test');
      }
    } catch (error) {
      setError('Failed to publish test');
      }
  };

  const handleAddQuestion = () => {
    // Allow questions with only images for certain question types
    const hasQuestionText = currentQuestion.question.trim();
    const hasQuestionImage = currentQuestion.questionImage || currentQuestion.questionImageUrl;
    
    if (!hasQuestionText && !hasQuestionImage) {
      setError('Please enter a question text or upload a question image.');
      return;
    }

    // Use test-level marking scheme as default if question doesn't have one explicitly set
    const defaultMarkingScheme = testData.markingScheme || { correct: 4, incorrect: -1, partial: 0 };
    const questionToAdd = {
      ...currentQuestion,
      id: currentQuestion.id || `q-${Date.now()}`,
      exam: testData.exam,
      // Use question's marking scheme if explicitly set, otherwise use test-level default
      markingScheme: currentQuestion.markingScheme || defaultMarkingScheme
    };

    // If editing a question, update it instead of adding a new one
    if (editingQuestionIndex !== null && editingQuestionIndex >= 0) {
      setTestData(prev => ({
        ...prev,
        questions: (prev.questions || []).map((q, i) => (i === editingQuestionIndex ? questionToAdd : q))
      }));
      setEditingQuestionIndex(null);
      setSuccess('Question updated');
    } else {
      setTestData(prev => {
        const currentQuestions = prev.questions || [];
        return {
          ...prev,
          questions: [...currentQuestions, questionToAdd],
          totalQuestions: currentQuestions.length + 1
        };
      });
    }

    // Reset question form with test-level marking scheme as default
    const resetMarkingScheme = testData.markingScheme || { correct: 4, incorrect: -1, partial: 0 };
    setCurrentQuestion({
      id: '',
      type: 'multiple-choice',
      question: '',
      questionImage: null,
      questionImageUrl: '',
      latex: '',
      diagramImageUrl: '',
      mathpixAnswerText: '',
      mathpixMarkdown: '',
      options: ['', '', '', ''],
      optionImages: [null, null, null, null],
      optionImageUrls: ['', '', '', ''],
      correctAnswer: 0,
      explanation: '',
      difficulty: 'Medium',
      topic: '',
      allowMultiple: false,
      passage: '',
      setContent: '',
      passageGroupId: '',
      setGroupId: '',
      questionNumberInGroup: 1,
      markingScheme: { ...resetMarkingScheme }
    });
  };

  const handleRemoveQuestion = (index) => {
    setTestData(prev => {
      const currentQuestions = prev.questions || [];
      return {
        ...prev,
        questions: currentQuestions.filter((_, i) => i !== index),
        totalQuestions: Math.max(0, currentQuestions.length - 1)
      };
    });
    if (editingQuestionIndex === index) {
      // If the removed question was being edited, reset edit mode
      setEditingQuestionIndex(null);
      // Use test-level marking scheme as default
      const resetMarkingScheme = testData.markingScheme || { correct: 4, incorrect: -1, partial: 0 };
      setCurrentQuestion({
        id: '',
        type: 'multiple-choice',
        question: '',
        questionImage: null,
        questionImageUrl: '',
        latex: '',
        diagramImageUrl: '',
        mathpixAnswerText: '',
        mathpixMarkdown: '',
        options: ['', '', '', ''],
        optionImages: [null, null, null, null],
        optionImageUrls: ['', '', '', ''],
        correctAnswer: 0,
        explanation: '',
        difficulty: 'Medium',
        topic: '',
        allowMultiple: false,
        passage: '',
        setContent: '',
        passageGroupId: '',
        setGroupId: '',
        questionNumberInGroup: 1,
        markingScheme: { ...resetMarkingScheme }
      });
    }
  };

  const handleEditQuestion = (index) => {
    if (!testData.questions || !Array.isArray(testData.questions)) {
      return;
    }
    const q = testData.questions[index];
    if (!q) return;
    // Use test-level marking scheme as default if question doesn't have one
    const defaultMarkingScheme = testData.markingScheme || { correct: 4, incorrect: -1, partial: 0 };
    // Normalize fields for editing form
    const normalized = {
      id: q.id || `q-${Date.now()}`,
      type: q.type || 'multiple-choice',
      question: q.question || '',
      questionImage: null,
      questionImageUrl: q.questionImageUrl || '',
      latex: q.latex || '',
      diagramImageUrl: q.diagramImageUrl || '',
      mathpixAnswerText: q.mathpixAnswerText || '',
      mathpixMarkdown: q.mathpixMarkdown || '',
      options: Array.isArray(q.options) ? [...q.options] : ['', '', '', ''],
      optionImages: [null, null, null, null],
      optionImageUrls: Array.isArray(q.optionImageUrls) ? [...q.optionImageUrls] : ['', '', '', ''],
      correctAnswer: q.correctAnswer ?? 0,
      explanation: q.explanation || '',
      difficulty: q.difficulty || 'Medium',
      topic: q.topic || '',
      allowMultiple: q.allowMultiple || q.type === 'multiple-choice-multi',
      markingScheme: q.markingScheme || { ...defaultMarkingScheme }
    };
    setCurrentQuestion(normalized);
    setEditingQuestionIndex(index);
  };

  // Helper function to extract plain text from HTML
  const extractTextFromHTML = (html) => {
    if (!html) return '';
    const tempDiv = document.createElement('div');
    tempDiv.innerHTML = html;
    return tempDiv.textContent || tempDiv.innerText || '';
  };

  // Translation function using multiple fallback methods
  const translateToHindi = async (text) => {
    if (!text || !text.trim()) return '';
    
    // Extract plain text from HTML if needed
    const plainText = extractTextFromHTML(text);
    if (!plainText.trim()) return '';
    
    // Try multiple translation methods in order
    const translationMethods = [
      // Method 1: Direct Google Translate API
      async () => {
        const response = await fetch(
          `https://translate.googleapis.com/translate_a/single?client=gtx&sl=en&tl=hi&dt=t&q=${encodeURIComponent(plainText)}`,
          {
            method: 'GET',
            mode: 'cors',
            headers: {
              'Accept': 'application/json',
            },
          }
        );
        if (!response.ok) throw new Error(`HTTP ${response.status}`);
        const data = await response.json();
        if (data && data[0] && data[0][0] && data[0][0][0]) {
          return data[0].map(item => item[0]).join('');
        }
        throw new Error('Invalid response format');
      },
      
      // Method 2: CORS proxy with Google Translate
      async () => {
        const proxyUrl = `https://api.allorigins.win/get?url=${encodeURIComponent(`https://translate.googleapis.com/translate_a/single?client=gtx&sl=en&tl=hi&dt=t&q=${encodeURIComponent(plainText)}`)}`;
        const response = await fetch(proxyUrl);
        if (!response.ok) throw new Error(`HTTP ${response.status}`);
        const proxyData = await response.json();
        const translatedData = JSON.parse(proxyData.contents);
        if (translatedData && translatedData[0] && translatedData[0][0] && translatedData[0][0][0]) {
          return translatedData[0].map(item => item[0]).join('');
        }
        throw new Error('Invalid response format');
      },
      
      // Method 3: Alternative CORS proxy
      async () => {
        const proxyUrl = `https://cors-anywhere.herokuapp.com/https://translate.googleapis.com/translate_a/single?client=gtx&sl=en&tl=hi&dt=t&q=${encodeURIComponent(plainText)}`;
        const response = await fetch(proxyUrl);
        if (!response.ok) throw new Error(`HTTP ${response.status}`);
        const data = await response.json();
        if (data && data[0] && data[0][0] && data[0][0][0]) {
          return data[0].map(item => item[0]).join('');
        }
        throw new Error('Invalid response format');
      },
    ];
    
    // Try each method until one succeeds
    for (let i = 0; i < translationMethods.length; i++) {
      try {
        const result = await translationMethods[i]();
        return result;
      } catch (error) {
        if (i === translationMethods.length - 1) {
          // All methods failed
          throw new Error(`All translation methods failed. Last error: ${error.message}`);
        }
      }
    }
    
    return plainText; // Fallback to original text
  };

  const handleTranslate = async (index) => {
    // Open modal immediately, before any checks
    setShowTranslationModal(true);
    setError(null); // Clear previous errors
    setTranslatedQuestion(null); // Clear previous translation
    setTranslating(true);
    
    if (!testData.questions || !Array.isArray(testData.questions)) {
      setError('No questions available to translate.');
      setTranslating(false);
      return;
    }
    const question = testData.questions[index];
    if (!question) {
      setError('Question not found.');
      setTranslating(false);
      return;
    }

    // Check if question has content
    if (!question.question || !question.question.trim()) {
      setError('Question text is empty. Please add question text first.');
      setTranslating(false);
      return;
    }
    
    try {
      // Translate question text
      const translatedQuestionText = await translateToHindi(question.question || '');
      
      // Translate options if they exist
      let translatedOptions = [];
      if (question.options && Array.isArray(question.options)) {
        translatedOptions = await Promise.all(
          question.options.map(async (option, optIdx) => {
            if (option && option.trim()) {
              try {
                const translated = await translateToHindi(option);
                return translated;
              } catch (err) {
                return option; // Return original if translation fails
              }
            }
            return option;
          })
        );
      }
      
      // Translate explanation if it exists
      let translatedExplanation = '';
      if (question.explanation && question.explanation.trim()) {
        try {
          translatedExplanation = await translateToHindi(question.explanation);
        } catch (err) {
          translatedExplanation = question.explanation; // Return original if translation fails
        }
      }
      
      const translatedData = {
        ...question,
        question: translatedQuestionText || question.question,
        options: translatedOptions.length > 0 ? translatedOptions : question.options,
        explanation: translatedExplanation || question.explanation,
        originalIndex: index
      };
      
      // Set the translated question state
      setTranslatedQuestion(translatedData);
    } catch (error) {
      setError(`Failed to translate question: ${error.message}. Please check your internet connection and try again.`);
      // Keep modal open but show error
      setTranslatedQuestion(null);
    } finally {
      setTranslating(false);
    }
  };

  const updateMarkingScheme = (field, value) => {
    setCurrentQuestion(prev => ({
      ...prev,
      markingScheme: {
        ...prev.markingScheme,
        [field]: parseFloat(value) || 0
      }
    }));
  };

  const handleDragEnd = (event) => {
    const { active, over } = event;

    if (!active || !over || active.id === over.id) {
      return;
    }

    setTestData((prev) => {
      if (!prev.questions || !Array.isArray(prev.questions)) {
        return prev;
      }
      
      const oldIndex = prev.questions.findIndex((question) => question?.id === active.id);
      const newIndex = prev.questions.findIndex((question) => question?.id === over.id);

      if (oldIndex === -1 || newIndex === -1) {
        return prev;
      }

      return {
        ...prev,
        questions: arrayMove(prev.questions, oldIndex, newIndex),
      };
    });
  };

  // Bulk Operations
  const handleBulkImport = async () => {
    try {
      const lines = bulkData.split('\n').filter(line => line.trim());
      const newTests = lines.map((line, index) => {
        const [title, description, exam, difficulty, duration, questions] = line.split('|');
        return {
          id: `bulk-test-${Date.now()}-${index}`,
          title: title.trim(),
          description: description ? description.trim() : '',
          exam: exam ? exam.trim() : 'CAT',
          difficulty: difficulty ? difficulty.trim() : 'Medium',
          duration: duration ? parseInt(duration) : '',
          questions: questions ? JSON.parse(questions) : [],
          status: 'draft'
        };
      });
      
      const result = await bulkSaveTests(newTests);
      if (result.success) {
        setSuccess(`Successfully imported ${newTests.length} tests`);
        setBulkData('');
        setShowBulkImport(false);
      } else {
        setError('Failed to import tests');
      }
    } catch (error) {
      setError('Error importing bulk data. Please check the format.');
      }
  };

  const exportTests = () => {
    const csvData = tests.map(test => 
      `${test.title}|${test.description}|${test.exam}|${test.difficulty}|${test.duration}|${JSON.stringify(test.questions || [])}`
    ).join('\n');
    
    const blob = new Blob([csvData], { type: 'text/csv' });
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = 'tests_export.csv';
    a.click();
  };

  // Import JEE Mains 2025 Shift 1 test (legacy function - kept for backward compatibility)
  const handleImportJEEMains2025Shift1 = async () => {
    try {
      setLoading(true);
      setError(null);
      
      // Check if test already exists
      const testRef = doc(db, COLLECTIONS.TESTS, jeemains2025shift1Data.id);
      const testSnap = await getDoc(testRef);
      
      if (testSnap.exists()) {
        setError(`Test "${jeemains2025shift1Data.title}" already exists with ID: ${jeemains2025shift1Data.id}`);
        setLoading(false);
        return;
      }
      
      // Ensure questions are properly sequenced and have all required fields
      const processedQuestions = jeemains2025shift1Data.questions.map((q, index) => ({
        ...q,
        id: q.id || `q${index + 1}`, // Ensure proper ID
        // Ensure questionImageUrl and optionImageUrls exist
        questionImageUrl: q.questionImageUrl || '',
        optionImageUrls: Array.isArray(q.optionImageUrls) ? q.optionImageUrls : ['', '', '', ''],
        // Ensure markingScheme exists
        markingScheme: q.markingScheme || {
          correct: 4,
          incorrect: -1,
          partial: 0
        }
      }));
      
      // Create test data with properly sequenced questions
      const testData = {
        ...jeemains2025shift1Data,
        questions: processedQuestions, // Use processed questions
        totalQuestions: processedQuestions.length, // Ensure correct count
        createdAt: new Date(),
        updatedAt: new Date(),
        totalAttempts: 0,
        averageScore: 0,
        coachingId: coachingId || null
      };
      
      // Save test with specific ID
      await setDoc(testRef, testData);
      
      setSuccess(`Successfully imported ${jeemains2025shift1Data.title} with ${processedQuestions.length} questions! All LaTeX formulas and images are properly configured.`);
      setLoading(false);
      loadTests(); // Reload tests list
    } catch (error) {
      setError(`Failed to import JEE Mains test: ${error.message}`);
      setLoading(false);
    }
  };



  // Enhanced PDF Import with separate options for different parsing methods
  const importFromPdf = async (file) => {
    try {
      if (!file) return;
      
      setLoading(true);
      setError(null);
      
      const pdfjsLib = await loadPdfjsLib();

      const arrayBuffer = await file.arrayBuffer();
      const pdf = await pdfjsLib.getDocument({ data: arrayBuffer }).promise;
      
      // Extract text from all pages for text-based parsing
      let fullText = '';
      const pageTexts = [];
      
      for (let i = 1; i <= pdf.numPages; i++) {
        const page = await pdf.getPage(i);
        const content = await page.getTextContent();
        
        const pageText = content.items
          .map(item => (item && typeof item.str === 'string' ? item.str : ''))
          .join('\n')
          .replace(/[\u00A0\t]+/g, ' ')
          .replace(/\r/g, '\n');
        
        pageTexts.push(pageText);
        fullText += '\n' + pageText;
      }

      // Use regex parser for text-based PDFs
      const regexParser = new RegexQuestionParser();
      const parseResult = await regexParser.parseQuestions(fullText);
      
      if (!parseResult.success || parseResult.questions.length === 0) {
        setError(`Could not detect questions from PDF. ${parseResult.error || 'The PDF might have complex formatting or be corrupted.'}`);
        setLoading(false);
        return;
      }

      // Convert to test questions format
      const parsedQuestions = parseResult.questions.map((question, index) => ({
        id: `pdf-q-${Date.now()}-${index}`,
        type: 'multiple-choice',
        question: question.question,
        options: question.options.length >= 4 ? question.options.slice(0, 4) : [
          ...question.options,
          ...Array(4 - question.options.length).fill('')
        ],
        correctAnswer: (() => {
          if (question.correctAnswer) {
            const idx = ['A','B','C','D'].indexOf(question.correctAnswer);
            return idx >= 0 ? idx : 0;
          }
          return 0;
        })(),
        explanation: '',
        difficulty: 'Medium',
        topic: '',
        markingScheme: {
          correct: 4,
          incorrect: -1,
          partial: 0
        }
      }));

      // Update test data with parsed questions
      setTestData(prev => {
        const currentQuestions = prev.questions || [];
        return {
          ...prev,
          questions: [...currentQuestions, ...parsedQuestions],
          totalQuestions: currentQuestions.length + parsedQuestions.length
        };
      });

      setSuccess(`Successfully parsed ${parsedQuestions.length} questions from PDF. Please review and edit as needed.`);
      setLoading(false);
      
    } catch (error) {
      setError(`Failed to import PDF: ${error.message}`);
      setLoading(false);
    }
  };

  const textToHtml = (text = '') => {
    if (!text) return '';
    return text
      .split(/\n+/)
      .map((line) => line.trim())
      .filter(Boolean)
      .map((line) => `<p>${line}</p>`)
      .join('');
  };

  const applyMathpixResultToQuestion = (result) => {
    if (!result) return;

    setCurrentQuestion((prev) => {
      const optionsFromResult =
        Array.isArray(result.options) && result.options.length > 0
          ? [...result.options]
          : [...prev.options];

      const desiredLength =
        prev.allowMultiple || prev.type?.includes('multiple-choice')
          ? Math.max(4, optionsFromResult.length)
          : Math.max(optionsFromResult.length, 4);

      while (optionsFromResult.length < desiredLength) {
        optionsFromResult.push('');
      }

      const correctAnswer =
        typeof result.correctAnswerIndex === 'number' ? result.correctAnswerIndex : prev.correctAnswer;

      return {
        ...prev,
        question: textToHtml(result.questionText) || prev.question,
        explanation: textToHtml(result.solutionText) || prev.explanation,
        options: optionsFromResult,
        correctAnswer,
        latex: result.latex || prev.latex,
        diagramImageUrl: result.diagramImageUrl || prev.diagramImageUrl,
        questionImageUrl: result.diagramImageUrl || prev.questionImageUrl,
        mathpixAnswerText: result.correctAnswerText || prev.mathpixAnswerText,
        mathpixMarkdown: result.mathpixMarkdown || prev.mathpixMarkdown,
      };
    });
  };

  const handleMathpixImport = async (file) => {
    if (!file) return;
    try {
      setMathpixLoading(true);
      setMathpixStatus('Sending to Mathpix...');
      const result = await parseQuestionWithMathpix(file);
      applyMathpixResultToQuestion(result);
      setMathpixStatus('Imported with Mathpix. Review extracted fields before saving.');
    } catch (err) {
      setError(err.message || 'Mathpix import failed');
      setMathpixStatus('');
    } finally {
      setMathpixLoading(false);
    }
  };

  const handleMathpixFileChange = (event) => {
    const file = event.target.files && event.target.files[0];
    if (file) {
      handleMathpixImport(file);
    }
    if (event.target) {
      event.target.value = '';
    }
  };

  const importPdfWithMathpix = async (file) => {
    try {
      if (!file) return;
      setMathpixPdfLoading(true);
      setError(null);
      setSuccess(null);
      setMathpixStatus('Sending PDF pages to Mathpix...');

      const pdfjsLib = await loadPdfjsLib();
      const arrayBuffer = await file.arrayBuffer();
      const pdf = await pdfjsLib.getDocument({ data: arrayBuffer }).promise;

      const regexParser = new RegexQuestionParser();
      const newQuestions = [];

      for (let i = 1; i <= pdf.numPages; i++) {
        const page = await pdf.getPage(i);
        const viewport = page.getViewport({ scale: 2 });
        const canvas = document.createElement('canvas');
        const context = canvas.getContext('2d');
        canvas.width = viewport.width;
        canvas.height = viewport.height;
        await page.render({ canvasContext: context, viewport }).promise;
        const dataUrl = canvas.toDataURL('image/png');

        const mathpixResult = await parseQuestionWithMathpixFromDataUrl(dataUrl);
        const combinedText = [
          mathpixResult.mathpixMarkdown,
          mathpixResult.questionText,
          mathpixResult.solutionText,
        ]
          .filter(Boolean)
          .join('\n');

        const parseResult = regexParser.parseQuestions(combinedText);
        if (parseResult.success && parseResult.questions.length > 0) {
          parseResult.questions.forEach((question, index) => {
            newQuestions.push({
              id: `mathpix-pdf-q-${Date.now()}-${i}-${index}`,
              type: 'multiple-choice',
              question: textToHtml(question.question || mathpixResult.questionText || ''),
              options: question.options.length >= 4
                ? question.options.slice(0, 4)
                : [...question.options, ...Array(Math.max(0, 4 - question.options.length)).fill('')],
              correctAnswer: (() => {
                if (typeof question.correctAnswer === 'string' && question.correctAnswer.trim()) {
                  const idx = ['A', 'B', 'C', 'D'].indexOf(question.correctAnswer.trim().toUpperCase());
                  if (idx >= 0) return idx;
                }
                if (typeof mathpixResult.correctAnswerIndex === 'number') return mathpixResult.correctAnswerIndex;
                return 0;
              })(),
              explanation: textToHtml(question.solution || mathpixResult.solutionText || ''),
              difficulty: 'Medium',
              topic: '',
              markingScheme: {
                correct: 4,
                incorrect: -1,
                partial: 0
              },
              latex: mathpixResult.latex || '',
              diagramImageUrl: mathpixResult.diagramImageUrl || '',
              questionImageUrl: mathpixResult.diagramImageUrl || '',
              mathpixAnswerText: mathpixResult.correctAnswerText || '',
              mathpixMarkdown: mathpixResult.mathpixMarkdown || ''
            });
          });
        } else {
          newQuestions.push({
            id: `mathpix-pdf-q-${Date.now()}-${i}-0`,
            type: 'multiple-choice',
            question: textToHtml(mathpixResult.questionText || ''),
            options: mathpixResult.options.length >= 4
              ? mathpixResult.options.slice(0, 4)
              : [...mathpixResult.options, ...Array(Math.max(0, 4 - mathpixResult.options.length)).fill('')],
            correctAnswer: typeof mathpixResult.correctAnswerIndex === 'number' ? mathpixResult.correctAnswerIndex : 0,
            explanation: textToHtml(mathpixResult.solutionText || ''),
            difficulty: 'Medium',
            topic: '',
            markingScheme: {
              correct: 4,
              incorrect: -1,
              partial: 0
            },
            latex: mathpixResult.latex || '',
            diagramImageUrl: mathpixResult.diagramImageUrl || '',
            questionImageUrl: mathpixResult.diagramImageUrl || '',
            mathpixAnswerText: mathpixResult.correctAnswerText || '',
            mathpixMarkdown: mathpixResult.mathpixMarkdown || ''
          });
        }
      }

      if (newQuestions.length === 0) {
        setError('Mathpix could not extract any questions from this PDF. Try the standard PDF import or a clearer scan.');
        setMathpixStatus('');
        return;
      }

      setTestData(prev => {
        const currentQuestions = prev.questions || [];
        return {
          ...prev,
          questions: [...currentQuestions, ...newQuestions],
          totalQuestions: currentQuestions.length + newQuestions.length
        };
      });

      setSuccess(`Imported ${newQuestions.length} questions via Mathpix PDF.`);
      setMathpixStatus('');
    } catch (err) {
      setError(err.message || 'Mathpix PDF import failed');
      setMathpixStatus('');
    } finally {
      setMathpixPdfLoading(false);
    }
  };





  // Function to extract images from PDF pages
  const extractImagesFromPDF = async (pdf, pageNumber) => {
    try {
      if (!pdf) return [];
      
      const page = await pdf.getPage(pageNumber);
      const viewport = page.getViewport({ scale: 2.0 });
      
      // Create canvas for image extraction
      const canvas = document.createElement('canvas');
      const context = canvas.getContext('2d');
      canvas.height = viewport.height;
      canvas.width = viewport.width;
      
      // Render page to canvas
      await page.render({
        canvasContext: context,
        viewport: viewport
      }).promise;
      
      // Convert canvas to image data URL
      const imageDataUrl = canvas.toDataURL('image/png');
      
      return [{
        dataUrl: imageDataUrl,
        pageNumber: pageNumber,
        width: viewport.width,
        height: viewport.height
      }];
    } catch (error) {
      return [];
    }
  };

  // Enhanced function to detect if text contains diagram references or mathematical expressions
  const detectDiagramReferences = (text) => {
    const diagramPatterns = [
      /figure\s*\d+/i,
      /diagram\s*\d+/i,
      /graph\s*\d+/i,
      /image\s*\d+/i,
      /see\s+figure/i,
      /refer\s+to\s+figure/i,
      /as\s+shown\s+in\s+figure/i,
      /according\s+to\s+figure/i,
      /in\s+the\s+figure/i,
      /the\s+figure\s+shows/i,
      /from\s+the\s+figure/i,
      /based\s+on\s+figure/i,
      // Mathematical expression patterns
      /f\(x\)/i,
      /∫/g,
      /∑/g,
      /√/g,
      /π/g,
      /α|β|γ|δ|θ|λ|μ|σ|φ|ψ|ω/g,
      /lim\s*[x→]/i,
      /sin|cos|tan|cot|sec|csc/i,
      /log|ln|exp/i,
      /matrix|vector/i,
      /coordinate|axis|plane/i,
      /curve|parabola|ellipse|hyperbola/i,
      /triangle|circle|square|rectangle/i,
      /angle|degree|radian/i,
      /slope|gradient|derivative/i,
      /integral|differential/i,
      /equation|formula|expression/i
    ];
    
    return diagramPatterns.some(pattern => pattern.test(text));
  };

  // Function to detect mathematical expressions that might need diagrams
  const detectMathematicalContent = (text) => {
    const mathPatterns = [
      /[∫∑√π]/g,
      /[αβγδεζηθικλμνξοπρστυφχψω]/g,
      /f\([^)]+\)/g,
      /lim\s*[x→]/i,
      /sin|cos|tan|cot|sec|csc/i,
      /log|ln|exp/i,
      /matrix|vector/i,
      /coordinate|axis|plane/i,
      /curve|parabola|ellipse|hyperbola/i,
      /triangle|circle|square|rectangle/i,
      /angle|degree|radian/i,
      /slope|gradient|derivative/i,
      /integral|differential/i,
      /equation|formula|expression/i,
      /graph|plot|chart/i,
      /function|relation/i,
      /domain|range|codomain/i,
      /asymptote|tangent|normal/i,
      /vertex|focus|directrix/i,
      /radius|diameter|circumference/i,
      /area|volume|perimeter/i
    ];
    
    return mathPatterns.some(pattern => pattern.test(text));
  };

  // Enhanced options extraction with multiple strategies
  const extractEmbeddedOptions = (questionText) => {
    const options = [];
    
    // Strategy 1: Look for embedded options in the question text
    const embeddedPatterns = [
      /\((\d+)\)\s*([^(]+?)(?=\(\d+\)|$)/g,  // (1) option1 (2) option2
      /\((\d+)\)\s*([^(]+?)(?=\s*\(\d+\)|$)/g, // More flexible spacing
      /\((\d+)\)\s*([^)]+)/g,  // (1) option1) - simpler pattern
    ];
    
    for (const pattern of embeddedPatterns) {
      let match;
      while ((match = pattern.exec(questionText)) !== null) {
        const optionNumber = parseInt(match[1]);
        let optionText = match[2].trim();
        
        // Clean up the option text
        optionText = optionText.replace(/^\s*[.,;:]\s*/, ''); // Remove leading punctuation
        optionText = optionText.replace(/\s+$/, ''); // Remove trailing spaces
        
        if (optionText && optionText.length > 0 && optionText.length < 200) {
          // Avoid very long "options" that are likely question text
          options.push(optionText);
        }
      }
      
      // If we found options with this pattern, use them
      if (options.length > 0) {
        break;
      }
    }
    
    // Strategy 2: If we didn't find enough options, look for common JEE option patterns
    if (options.length < 2) {
      // Look for patterns like "1 - loge 2", "loge 2", "1 + loge 2", "2 loge 2 - 1"
      const commonOptions = [
        "1 - loge 2", "loge 2", "1 + loge 2", "2 loge 2 - 1",
        "2√14", "√14", "3√7", "2√7",
        "3/16", "5/8", "3/8", "1/8",
        "48x + 25y = 169", "5x + 16y = 31", "25x + 101y = 176", "4x + 122y = 134",
        "Neither one-one nor onto", "Onto but not one-one", "Both one-one and onto", "One-one but not onto",
        "t₁ > t₂", "t₁ = t₂", "t₁ < t₂", "t₁ = 2t₂",
        "3 cm", "2 cm", "4 cm", "1 cm",
        "0.23 mm", "0.33 mm", "0.63 mm", "0.46 mm",
        "zero", "5π", "40π", "10π"
      ];
      
      // Check if any of these common options appear in the question text
      commonOptions.forEach(option => {
        if (questionText.includes(option) && !options.includes(option)) {
          options.push(option);
        }
      });
    }
    
    // Strategy 3: If still not enough options, try to extract from mathematical expressions
    if (options.length < 2) {
      // Look for mathematical expressions that could be options
      const mathPatterns = [
        /(\d+\.?\d*)\s*[+\-×÷]\s*(\d+\.?\d*)/g,  // Numbers with operations
        /(\d+\.?\d*)\s*[a-zA-Z]/g,  // Numbers with variables
        /[a-zA-Z]\s*(\d+\.?\d*)/g,  // Variables with numbers
        /(\d+\.?\d*)\s*[a-zA-Z]\s*(\d+\.?\d*)/g,  // Numbers with variables and numbers
      ];
      
      let match;
      for (const pattern of mathPatterns) {
        while ((match = pattern.exec(questionText)) !== null) {
          const optionText = match[0].trim();
          if (optionText && optionText.length > 0 && optionText.length < 50 && !options.includes(optionText)) {
            options.push(optionText);
          }
        }
      }
    }
    
    return options;
  };

  // Remove embedded options from question text
  const removeEmbeddedOptions = (questionText) => {
    return questionText.replace(/\(\d+\)\s*[^(]+?(?=\(\d+\)|$)/g, '').trim();
  };

  // Associate options with questions based on context
  const associateOptionsWithQuestion = (questionNumber, questionText, allOptions) => {
    const associatedOptions = [];
    
    // For specific questions, use known option patterns
    const questionSpecificOptions = {
      4: ["1 - loge 2", "loge 2", "1 + loge 2", "2 loge 2 - 1"],
      5: ["48x + 25y = 169", "5x + 16y = 31", "25x + 101y = 176", "4x + 122y = 134"],
      6: ["44", "22", "33", "55"],
      7: ["39", "19", "29", "23"],
      8: ["6", "1"],
      9: ["6", "8", "9", "7"],
      10: ["3", "4", "1", "6"],
      11: ["2√14", "√14", "3√7", "2√7"],
      12: ["1", "0", "2", "3"],
      13: ["Neither one-one nor onto", "Onto but not one-one", "Both one-one and onto", "One-one but not onto"],
      14: ["t₁ > t₂", "t₁ = t₂", "t₁ < t₂", "t₁ = 2t₂"],
      15: ["525", "510", "515", "505"],
      16: ["22", "20", "21", "19"],
      17: ["420", "360", "160", "280"],
      18: ["16", "25", "9", "36"],
      19: ["7", "9", "8", "6"],
      20: ["3/16", "5/8", "3/8", "1/8"]
    };
    
    if (questionSpecificOptions[questionNumber]) {
      return questionSpecificOptions[questionNumber];
    }
    
    // For other questions, try to find options from the text context
    const contextOptions = [];
    
    // Look for numbers that appear in the question text
    const numbers = questionText.match(/\d+/g);
    if (numbers) {
      numbers.forEach(num => {
        if (num.length <= 3 && !contextOptions.includes(num)) {
          contextOptions.push(num);
        }
      });
    }
    
    // Look for mathematical expressions
    const expressions = questionText.match(/[a-zA-Z0-9\s+\-×÷]+/g);
    if (expressions) {
      expressions.forEach(expr => {
        if (expr.length > 2 && expr.length < 30 && !contextOptions.includes(expr.trim())) {
          contextOptions.push(expr.trim());
        }
      });
    }
    
    return contextOptions.length > 0 ? contextOptions : allOptions.slice(0, 4);
  };

  // Enhanced JEE Main question parsing function with image extraction
  const parseJEEQuestionsFromText = async (text, pdf = null) => {
    const questionBlocks = [];
    
    // Enhanced text cleaning that preserves mathematical symbols and special characters
    const normalizedText = text
      .replace(/[\u00A0\u2000-\u200F\u2028-\u202F\u205F-\u206F\u3000]/g, ' ') // Replace various spaces
      .replace(/\r\n/g, '\n')
      .replace(/\r/g, '\n')
      .replace(/\n{3,}/g, '\n\n') // Reduce multiple newlines
      .replace(/\s+/g, ' ') // Normalize spaces
      .trim();
    
    // Split by question patterns first to handle the specific JEE format
    const questionSections = normalizedText.split(/(?=Q\s*\d+[\.\)])/i);
    
    // Extract all potential options from the entire text for later association
    const extractAllOptionsFromText = (text) => {
      const options = [];
      const lines = text.split('\n');
      
      // Look for common option patterns throughout the text
      lines.forEach((line, index) => {
        // Pattern 1: Lines with multiple numbers that could be options
        if (line.match(/^\d+\s+\d+\s+\d+\s+\d+/) || line.match(/^\d+\s+\d+\s+\d+/)) {
          const numbers = line.match(/\d+/g);
          if (numbers && numbers.length >= 2) {
            numbers.forEach(num => {
              if (num.length <= 6 && !options.includes(num)) {
                options.push(num);
              }
            });
          }
        }
        
        // Pattern 2: Lines with mathematical expressions
        if (line.match(/[a-zA-Z]\s*[+\-×÷]\s*[a-zA-Z]/) || line.match(/\d+\s*[+\-×÷]\s*\d+/)) {
          const expressions = line.match(/[a-zA-Z0-9\s+\-×÷]+/g);
          if (expressions) {
            expressions.forEach(expr => {
              if (expr.length > 2 && expr.length < 50 && !options.includes(expr.trim())) {
                options.push(expr.trim());
              }
            });
          }
        }
        
        // Pattern 3: Lines with units (mm, cm, etc.)
        if (line.match(/\d+\.?\d*\s*(mm|cm|m|kg|g|s|N|J|V|A|Ω|Hz|K|°C)/)) {
          const units = line.match(/\d+\.?\d*\s*(mm|cm|m|kg|g|s|N|J|V|A|Ω|Hz|K|°C)/g);
          if (units) {
            units.forEach(unit => {
              if (!options.includes(unit.trim())) {
                options.push(unit.trim());
              }
            });
          }
        }
        
        // Pattern 4: Lines with fractions
        if (line.match(/\d+\/\d+/)) {
          const fractions = line.match(/\d+\/\d+/g);
          if (fractions) {
            fractions.forEach(frac => {
              if (!options.includes(frac)) {
                options.push(frac);
              }
            });
          }
        }
        
        // Pattern 5: Lines with square roots
        if (line.match(/√\d+/)) {
          const roots = line.match(/√\d+/g);
          if (roots) {
            roots.forEach(root => {
              if (!options.includes(root)) {
                options.push(root);
              }
            });
          }
        }
      });
      
      return options;
    };
    
    const allOptions = extractAllOptionsFromText(normalizedText);
    
    questionSections.forEach((section, sectionIndex) => {
      if (!section.trim()) return;
      
      const lines = section.split(/\n+/).map(s => s.trim()).filter(Boolean);
      let current = null;
      let questionNumber = 0;
      
      // Skip common exam headers
      const skipPatterns = [
        /^(JEE|NEET|CAT|GATE|UPSC|SSC|IBPS|SBI|Railway|Bank)/i,
        /^(Previous|Year|Paper|Answer|Key|Solution)/i,
        /^(Section|Part|Chapter|Topic)/i,
        /^(Instructions|Directions|Note)/i,
        /^(Time|Duration|Marks|Total)/i,
        /^(Page|PAGE)/i,
        /^(Copyright|COPYRIGHT)/i,
        /^(2025|24 Jan Shift 2|JEE Main 2025|January JEE Main Previous Year Paper)/i,
        /^(Mathon Go|ANSWER KEYS)/i,
      ];
      
      lines.forEach((line, index) => {
        // Skip empty lines and common headers
        if (!line || line.length < 3) return;
        
        if (skipPatterns.some(pattern => pattern.test(line))) return;
        
        // Enhanced patterns for JEE-style questions
        const questionPatterns = [
          /^\s*Q\s*(\d+)\s*[\.\)]\s+(.+)/i,                   // Q 1. Question text
          /^\s*Q\s*(\d+)\s*[\.\)]\s*[A-Za-z]/,                // Q 1. A (for JEE style)
          /^\s*(\d+)\s*[\.\)\-]\s+(.+)/,                     // 1. / 1) / 1 -
          /^\s*Question\s*(\d+)\s*[\.\)\-]\s+(.+)/i,         // Question 1.
          /^\s*(\d+)\s*[\.\)]\s+(.+)/,                       // 1. Question text
        ];
        
        const optionPatterns = [
          /^\s*([A-D])\s*[\.\)]\s+(.+)/,                     // A) Option or A. Option
          /^\s*\(([A-D])\)\s+(.+)/,                          // (A) Option
          /^\s*([A-D])\s*[\.\)]?\s+(.+)/,                    // A Option (tolerant)
        ];
        
        const answerPatterns = [
          /^Answer\s*[:\-]\s*([A-D])/i,
          /^Correct\s*Answer\s*[:\-]\s*([A-D])/i,
          /^Key\s*[:\-]\s*([A-D])/i,
          /^Solution\s*[:\-]\s*([A-D])/i,
          /^Ans\s*[:\-]\s*([A-D])/i,
          /^Answer\s*Key\s*[:\-]\s*([A-D])/i,
        ];
        
        // Check if line matches question pattern
        let questionMatch = null;
        for (const pattern of questionPatterns) {
          questionMatch = line.match(pattern);
          if (questionMatch) break;
        }
        
        if (questionMatch) {
          // Save previous question if exists
          if (current && current.prompt.trim()) {
            questionBlocks.push(current);
          }
          
          // Start new question
          questionNumber = parseInt(questionMatch[1]) || questionNumber + 1;
          const fullQuestionText = questionMatch[2] ? questionMatch[2].trim() : line.trim();
          
          // Extract embedded options from the complete question text
          const embeddedOptions = extractEmbeddedOptions(fullQuestionText);
          const cleanPrompt = removeEmbeddedOptions(fullQuestionText);
          
          // If we don't have enough options, try to associate them from the global options
          let finalOptions = embeddedOptions;
          if (embeddedOptions.length < 2) {
            const associatedOptions = associateOptionsWithQuestion(questionNumber, fullQuestionText, allOptions);
            finalOptions = associatedOptions.length > embeddedOptions.length ? associatedOptions : embeddedOptions;
          }
          
          current = {
            prompt: cleanPrompt,
            options: finalOptions,
            questionNumber: questionNumber
          };
          return;
        }
        
        // Check if line matches option pattern
        let optionMatch = null;
        for (const pattern of optionPatterns) {
          optionMatch = line.match(pattern);
          if (optionMatch) break;
        }
        
        if (optionMatch && current) {
          current.options.push(optionMatch[2].trim());
          return;
        }
        
        // Detect answer patterns
        let answerMatch = null;
        for (const pattern of answerPatterns) {
          answerMatch = line.match(pattern);
          if (answerMatch) break;
        }
        
        if (answerMatch && current) {
          current.correctChoice = answerMatch[1].toUpperCase();
          return;
        }
        
        // If we're in a question and line doesn't match patterns, append to question text
        if (current && !/^\d+[\.\)]/.test(line) && !/^[A-D][\.\)]/.test(line)) {
          // Only append if it looks like question content
          if (line.length > 10 && !/^(Answer|Solution|Key|Correct)/i.test(line)) {
            current.prompt += ' ' + line;
          }
        }
      });
      
      // Add the last question if exists
      if (current && current.prompt.trim()) {
        questionBlocks.push(current);
      }
    });
    
    // Enhanced processing for embedded options with better pattern matching and image extraction
    const processedBlocks = await Promise.all(questionBlocks.map(async (block) => {
      const prompt = block.prompt;
      let cleanPrompt = prompt;
      let extractedOptions = [];
      let images = [];
      
      // Enhanced pattern to match embedded options with better handling of mathematical content
      // Pattern 1: Standard format (1) Option (2) Option (3) Option (4) Option
      const embeddedOptionsPattern = /\((\d+)\)\s*([^\(]+?)(?=\(\d+\)|$)/g;
      let match;
      
      while ((match = embeddedOptionsPattern.exec(prompt)) !== null) {
        const optionNumber = parseInt(match[1]);
        const optionText = match[2].trim();
        
        if (optionNumber >= 1 && optionNumber <= 4) {
          extractedOptions[optionNumber - 1] = optionText;
          // Remove this option from the prompt
          cleanPrompt = cleanPrompt.replace(match[0], '').trim();
        }
      }
      
      // Pattern 2: Handle cases where options might be separated by different patterns
      if (extractedOptions.filter(Boolean).length === 0) {
        // Try alternative patterns for mathematical expressions
        const altPatterns = [
          /\((\d+)\)\s*([^\(]+?)(?=\(\d+\)|$)/g,  // Standard pattern
          /(\d+)\)\s*([^\(]+?)(?=\d+\)|$)/g,      // Without opening parenthesis
          /(\d+)\s*[\.\)]\s*([^\(]+?)(?=\d+\s*[\.\)]|$)/g,  // With dots
          /(\d+)\s*[\.\)]\s*([^\(]+?)(?=\d+\s*[\.\)]|$)/g   // Alternative format
        ];
        
        for (const pattern of altPatterns) {
          let altMatch;
          const tempOptions = [];
          pattern.lastIndex = 0; // Reset regex state
          
          while ((altMatch = pattern.exec(prompt)) !== null) {
            const optionNumber = parseInt(altMatch[1]);
            const optionText = altMatch[2].trim();
            
            if (optionNumber >= 1 && optionNumber <= 4) {
              tempOptions[optionNumber - 1] = optionText;
            }
          }
          
          if (tempOptions.filter(Boolean).length > 0) {
            extractedOptions = tempOptions;
            // Remove matched options from prompt
            cleanPrompt = cleanPrompt.replace(pattern, '').trim();
            break;
          }
        }
      }
      
      // Enhanced image extraction for questions with mathematical content or diagrams
      if (pdf && (detectDiagramReferences(cleanPrompt) || detectMathematicalContent(cleanPrompt))) {
        try {
          // Extract images from the current page (assuming questions are on the same page)
          const currentPage = Math.floor(block.questionNumber / 10) + 1; // Estimate page based on question number
          images = await extractImagesFromPDF(pdf, currentPage);
          
          // If no images found on estimated page, try adjacent pages
          if (images.length === 0) {
            const adjacentPages = [currentPage - 1, currentPage + 1].filter(p => p > 0);
            for (const pageNum of adjacentPages) {
              const pageImages = await extractImagesFromPDF(pdf, pageNum);
              if (pageImages.length > 0) {
                images = pageImages;
                break;
              }
            }
          }
          
          // If still no images found, try extracting from all pages (for complex questions)
          if (images.length === 0 && pdf.numPages) {
            for (let pageNum = 1; pageNum <= Math.min(pdf.numPages, 5); pageNum++) {
              const pageImages = await extractImagesFromPDF(pdf, pageNum);
              if (pageImages.length > 0) {
                images = pageImages;
                break;
              }
            }
          }
        } catch (error) {
        }
      }
      
      // If we found embedded options, use them; otherwise use the original options
      const finalOptions = extractedOptions.filter(Boolean).length > 0 ? extractedOptions.filter(Boolean) : block.options;
      
      // Enhanced post-processing for text
      const processedPrompt = cleanPrompt.trim();
      const processedOptions = finalOptions.map(opt => opt.trim());
      
      return {
        ...block,
        prompt: processedPrompt,
        options: processedOptions,
        images: images
      };
    }));

    // Now try to associate mathematical content with questions based on specific patterns
    processedBlocks.forEach(block => {
      // Q3: "Let and. Then is equal to:" - needs A and B definitions
      if (block.questionNumber === 3) {
        const aDefinition = "A = {x ∈ (0, π) - {π/2} : log(2/π)|sin x| + log(2/π)|cos x| = 2}";
        const bDefinition = "B = {x ≥ 0 : √x(√x - 4) - 3|√x - 2| + 6 = 0}";
        block.prompt = block.prompt.replace('Let and', `Let ${aDefinition} and ${bDefinition}`);
      }
      // Q4: "The area of the region enclosed by the curves and -axis is:" - needs curve definitions
      else if (block.questionNumber === 4) {
        const curve1 = "y = ex";
        const curve2 = "y = |ex - 1|";
        block.prompt = block.prompt.replace('curves and -axis', `curves ${curve1}, ${curve2} and y-axis`);
      }
      // Q5: "The equation of the chord, of the ellipse, whose mid-point is is:" - needs ellipse equation and point
      else if (block.questionNumber === 5) {
        const ellipse = "x²/25 + y²/16 = 1";
        const point = "(3, 1)";
        block.prompt = block.prompt.replace('ellipse , whose mid-point is is :', `ellipse ${ellipse}, whose mid-point is ${point} is:`);
      }
      // Q6: "Let the points lie on or inside the triangle with sides and. Then the product of the smallest and the largest values of is equal to:" - needs triangle sides
      else if (block.questionNumber === 6) {
        const sides = "a, b, c";
        block.prompt = block.prompt.replace('sides and', `sides ${sides}`);
      }
      // Q7: "Let be a function which is differentiable at all points of its domain and satisfies the condition, with. Then is equal to:" - needs function definition
      else if (block.questionNumber === 7) {
        const functionDef = "f: (0, ∞) → R";
        const condition = "x²f'(x) = 2xf(x) + 3";
        const initialValue = "f(1) = 4";
        block.prompt = block.prompt.replace('Let be a function', `Let ${functionDef} be a function`);
        block.prompt = block.prompt.replace('satisfies the condition, with', `satisfies the condition ${condition}, with ${initialValue}`);
      }
      // Q8: "If, then the value of is:" - needs the equation
      else if (block.questionNumber === 8) {
        const equation = "7 = 5 + (1/7)(5 + α) + (1/7²)(5 + 2α) + (1/7³)(5 + 3α) + ...";
        block.prompt = block.prompt.replace('If, then', `If ${equation}, then`);
      }
      // Q9: "Let denote the greatest integer function, and let m and n respectively be the numbers of the points, where the function, is not continuous and not differentiable. Then is equal to:" - needs function definition
      else if (block.questionNumber === 9) {
        const functionDef = "f(x) = [x] + |x - 2|, -2 < x < 3";
        block.prompt = block.prompt.replace('where the function, is not continuous', `where the function ${functionDef} is not continuous`);
      }
      // Q10: "Let be a square matrix of order 2 with entries either 0 or 1. Let be the event that is an invertible matrix. Then the probability is:" - needs matrix and event definitions
      else if (block.questionNumber === 10) {
        const matrixDef = "A = [aᵢⱼ]";
        const eventDef = "E be the event that A is an invertible matrix";
        block.prompt = block.prompt.replace('Let be a square matrix', `Let ${matrixDef} be a square matrix`);
        block.prompt = block.prompt.replace('Let be the event that is an invertible matrix', `Let ${eventDef}`);
      }
      // Q11: "Let the position vectors of three vertices of a triangle be and. If the position vectors of the orthocenter and the circumcenter of the triangle are and respectively, then is equal to:" - needs vector definitions
      else if (block.questionNumber === 11) {
        const vectors = "4p⃗ + q⃗ - 3r⃗, -5p⃗ + q⃗ + 2r⃗, 2p⃗ - q⃗ + 2r⃗";
        const centers = "p⃗ + q⃗ + r⃗, 4αp⃗ + βq⃗ + γr⃗";
        block.prompt = block.prompt.replace('three vertices of a triangle be and', `three vertices of a triangle be ${vectors}`);
        block.prompt = block.prompt.replace('orthocenter and the circumcenter of the triangle are and', `orthocenter and the circumcenter of the triangle are ${centers}`);
      }
      // Q12: "Let and. Then the projection of on is:" - needs vector definitions
      else if (block.questionNumber === 12) {
        const vectorA = "a⃗ = 3î - ĵ + 2k̂";
        const vectorB = "b⃗ = a⃗ × (î - 2k̂)";
        const vectorC = "c⃗ = b⃗ × k̂";
        block.prompt = block.prompt.replace('Let and. Then', `Let ${vectorA} and ${vectorB}. Then`);
        block.prompt = block.prompt.replace('projection of on is', `projection of ${vectorC} on a⃗ is`);
      }
      // Q13: "The number of real solution(s) of the equation is:" - needs equation
      else if (block.questionNumber === 13) {
        const equation = "x² + 3x + 2 = min{|x - 3|, |x + 2|}";
        block.prompt = block.prompt.replace('The number of real solution(s) of the equation is', `The number of real solution(s) of the equation ${equation} is`);
      }
      // Q14: "The function defined by is:" - needs function definition
      else if (block.questionNumber === 14) {
        const functionDef = "f: (-∞, ∞) → (-∞, 1), f(x) = (2ˣ - 2⁻ˣ)/(2ˣ + 2⁻ˣ)";
        block.prompt = block.prompt.replace('The function defined by is', `The function ${functionDef} is`);
      }
      // Q15: "In an arithmetic progression, if S₄₀ = 1030 and S₁₂ = 57, then S₃₀ - S₁₀ is equal to:" - needs progression definition
      else if (block.questionNumber === 15) {
        const progression = "S₄₀ = 1030 and S₁₂ = 57";
        block.prompt = block.prompt.replace('if and, then', `if ${progression}, then`);
      }
      // Q16: "Suppose A and B are the coefficients of and terms respectively in the binomial expansion of. If, then n is equal to:" - needs expansion definition
      else if (block.questionNumber === 16) {
        const expansion = "(1 + x)^(2n-1)";
        const condition = "A = 5B";
        block.prompt = block.prompt.replace('binomial expansion of. If, then', `binomial expansion of ${expansion}. If ${condition}, then`);
      }
      // Q17: "Let be the largest open interval in which the function is strictly increasing and (b, c) be the largest open interval, in which the function is strictly decreasing. Then is equal to:" - needs function definitions
      else if (block.questionNumber === 17) {
        const function1 = "f(x) = 2logₑ(x - 2) - x² + ax + 1";
        const function2 = "g(x) = (x - 1)³(x + 2 - a)²";
        block.prompt = block.prompt.replace('function is strictly increasing', `function ${function1} is strictly increasing`);
        block.prompt = block.prompt.replace('function is strictly decreasing', `function ${function2} is strictly decreasing`);
      }
      // Q18: "For some, let. Then is equal to:" - needs function definition
      else if (block.questionNumber === 18) {
        const functionDef = "f(x) = (a + sin x)/x, x ≠ 0";
        const limit = "lim(x→0) f(x) = λ + μa + νb";
        block.prompt = block.prompt.replace('For some, let. Then', `For some a, b, let ${functionDef}. Then`);
        block.prompt = block.prompt.replace('is equal to', `${limit} is equal to`);
      }
      // Q19: "If the equation of the parabola with vertex and the directrix is, then is equal to:" - needs parabola definition
      else if (block.questionNumber === 19) {
        const vertex = "V(3/2, 3)";
        const directrix = "x + 2y = 0";
        const equation = "αx² + βy² - γxy - 30x - 60y + 225 = 0";
        block.prompt = block.prompt.replace('vertex and the directrix is, then', `vertex ${vertex} and the directrix ${directrix} is ${equation}, then`);
      }
      // Q20: "If, then the expression is equal to:" - needs the equation
      else if (block.questionNumber === 20) {
        const equation = "cot⁻¹{β + (1+β²)/(α-β)} + cot⁻¹{γ + (1+γ²)/(β-γ)} + cot⁻¹{α + (1+α²)/(γ-α)}";
        block.prompt = block.prompt.replace('If, then', `If α > β > γ > 0, then`);
        block.prompt = block.prompt.replace('the expression is equal to', `the expression ${equation} is equal to`);
      }
    });
    
    return processedBlocks;
  };












  const filteredTests = (tests || []).filter(test => {
    if (!test) return false;
    const matchesSearch = (test.title || '').toLowerCase().includes(searchTerm.toLowerCase()) ||
                         (test.description || '').toLowerCase().includes(searchTerm.toLowerCase());
    const matchesExam = !filterExam || test.exam === filterExam;
    const matchesStatus = !filterStatus || test.status === filterStatus;
    
    return matchesSearch && matchesExam && matchesStatus;
  });

  const renderQuestionForm = () => {
    return (
      <div className="bg-white rounded-lg shadow-md p-6 mb-6">
        <h3 className="text-lg font-semibold mb-4">{editingQuestionIndex !== null ? 'Edit Question' : 'Add Question'}</h3>
        
                 <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-4">
           <div>
             <label className="block text-sm font-medium text-gray-700 mb-2">
               Question Type
             </label>
             <select
               value={currentQuestion.type}
               onChange={(e) => setCurrentQuestion(prev => ({
                 ...prev,
                 type: e.target.value,
                 allowMultiple: e.target.value === 'multiple-choice-multi'
               }))}
               className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
             >
               {questionTypes.map(type => (
                 <option key={type.value} value={type.value}>
                   {type.label}
                 </option>
               ))}
             </select>
           </div>

           <div>
             <label className="block text-sm font-medium text-gray-700 mb-2">
               Difficulty
             </label>
             <select
               value={currentQuestion.difficulty}
               onChange={(e) => setCurrentQuestion(prev => ({ ...prev, difficulty: e.target.value }))}
               className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
             >
               <option value="Easy">Easy</option>
               <option value="Medium">Medium</option>
               <option value="Hard">Hard</option>
             </select>
           </div>

           <div>
             <label className="block text-sm font-medium text-gray-700 mb-2">
               Topic
             </label>
             <input
               type="text"
               value={currentQuestion.topic}
               onChange={(e) => setCurrentQuestion(prev => ({ ...prev, topic: e.target.value }))}
               className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
               placeholder="Enter topic (e.g., Algebra, Physics, etc.)"
             />
           </div>
         </div>

        {/* Passage/Set Content for RC and LRDI Set questions */}
        {(currentQuestion.type === 'rc' || currentQuestion.type === 'lrdi-set') && (
          <div className="mb-6 p-4 bg-blue-50 border border-blue-200 rounded-lg">
            <div className="flex items-center mb-3">
              <div className={`w-3 h-3 rounded-full mr-2 ${currentQuestion.type === 'rc' ? 'bg-green-500' : 'bg-purple-500'}`}></div>
              <label className="block text-sm font-medium text-gray-700">
                {currentQuestion.type === 'rc' ? '📖 Reading Comprehension Passage' : '📊 LRDI Set Content'}
              </label>
            </div>
            
            <div className="bg-white p-3 rounded border mb-3">
              <RichTextEditorWithImages
                value={currentQuestion.passage || currentQuestion.setContent || ''}
                onChange={(value) => setCurrentQuestion(prev => ({ 
                  ...prev, 
                  passage: currentQuestion.type === 'rc' ? value : prev.passage,
                  setContent: currentQuestion.type === 'lrdi-set' ? value : prev.setContent
                }))}
                placeholder={currentQuestion.type === 'rc' 
                  ? 'Enter the reading comprehension passage here. This will appear on the left side of the test interface for all questions in this group.' 
                  : 'Enter the LRDI set content (tables, charts, data, logical reasoning setup, etc.) here. This will appear on the left side for all questions in this set.'}
                label=""
                rows={8}
                showPreview={true}
                showToolbar={true}
                onImageUpload={async (file, imageId) => {
                  return { url: URL.createObjectURL(file) };
                }}
              />
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  🏷️ Group ID (Required for grouping)
                </label>
                <input
                  type="text"
                  value={currentQuestion.passageGroupId || currentQuestion.setGroupId || ''}
                  onChange={(e) => setCurrentQuestion(prev => ({ 
                    ...prev, 
                    passageGroupId: currentQuestion.type === 'rc' ? e.target.value : prev.passageGroupId,
                    setGroupId: currentQuestion.type === 'lrdi-set' ? e.target.value : prev.setGroupId
                  }))}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                  placeholder={currentQuestion.type === 'rc' ? 'e.g., rc-passage-1' : 'e.g., lrdi-set-1'}
                  required
                />
              </div>
              
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  📝 Question Number in Group
                </label>
                <input
                  type="number"
                  min="1"
                  value={currentQuestion.questionNumberInGroup || ''}
                  onChange={(e) => setCurrentQuestion(prev => ({ 
                    ...prev, 
                    questionNumberInGroup: parseInt(e.target.value) || 1
                  }))}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                  placeholder="1, 2, 3..."
                />
              </div>
            </div>

            <div className="mt-3 p-3 bg-yellow-50 border border-yellow-200 rounded">
              <p className="text-xs text-gray-600">
                <strong>💡 How it works:</strong>
                <br />• All questions with the same Group ID will share this {currentQuestion.type === 'rc' ? 'passage' : 'set content'}
                <br />• The {currentQuestion.type === 'rc' ? 'passage' : 'set'} appears on the left, individual questions on the right
                <br />• Create multiple questions with the same Group ID to build a complete {currentQuestion.type === 'rc' ? 'RC' : 'LRDI'} set
                <br />• Typical {currentQuestion.type === 'rc' ? 'RC passages have 3-5 questions' : 'LRDI sets have 3-4 questions'}
              </p>
            </div>
          </div>
        )}

        <div className="mb-4">
          <div className="flex items-center justify-between mb-2">
            <label className="block text-sm font-medium text-gray-700">
              Question Text
            </label>
            <div className="flex items-center space-x-2">
              <input
                ref={mathpixInputRef}
                type="file"
                accept="image/*,application/pdf"
                onChange={handleMathpixFileChange}
                className="hidden"
              />
              <button
                type="button"
                onClick={() => mathpixInputRef.current && mathpixInputRef.current.click()}
                disabled={mathpixLoading}
                className={`px-3 py-1.5 text-sm rounded-md border border-blue-200 bg-blue-50 text-blue-700 hover:bg-blue-100 flex items-center ${mathpixLoading ? 'opacity-70 cursor-not-allowed' : ''}`}
              >
                <Upload size={16} className="mr-2" />
                {mathpixLoading ? 'Processing...' : 'Import with Mathpix'}
              </button>
            </div>
          </div>
          <RichTextEditorWithImages
            value={currentQuestion.question || ''}
            onChange={(value) => setCurrentQuestion(prev => ({ ...prev, question: value }))}
            placeholder="Enter your question here... You can type text and insert images inline. Drag images to reposition them."
            label=""
            rows={4}
            showPreview={true}
            showToolbar={true}
            onImageUpload={async (file, imageId) => {
              // This will be called when an image is uploaded in the editor
              // The image will be uploaded during save, so we just return the blob URL for now
              return { url: URL.createObjectURL(file) };
            }}
          />
          {mathpixStatus && (
            <p className="text-sm text-blue-600 mt-2">{mathpixStatus}</p>
          )}
          {currentQuestion.latex && (
            <div className="mt-3 bg-gray-50 border border-gray-200 rounded-md p-3">
              <div className="text-xs font-semibold text-gray-700 mb-1">Extracted LaTeX</div>
              <pre className="text-xs text-gray-800 whitespace-pre-wrap break-words">{currentQuestion.latex}</pre>
            </div>
          )}
          {currentQuestion.mathpixAnswerText && (
            <div className="mt-2 text-xs text-gray-600">
              Detected answer text: <span className="font-semibold">{currentQuestion.mathpixAnswerText}</span>
            </div>
          )}
          {currentQuestion.diagramImageUrl && (
            <div className="mt-3">
              <div className="text-xs font-semibold text-gray-700 mb-1">Extracted Diagram</div>
              <img
                src={currentQuestion.diagramImageUrl}
                alt="Mathpix diagram"
                className="max-h-48 border border-gray-200 rounded-md"
              />
            </div>
          )}
        </div>

        {((currentQuestion.type === 'multiple-choice' || currentQuestion.type === 'multiple-choice-multi') ||
          currentQuestion.type === 'rc' || currentQuestion.type === 'lrdi-set') && (
          <div className="mb-4">
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Options
            </label>
            {currentQuestion.options.map((option, index) => (
              <div key={index} className="mb-4 p-4 border border-gray-200 rounded-lg">
                <div className="flex items-center mb-2">
                  <span className="text-sm font-medium text-gray-700 mr-2">Option {index + 1}:</span>
                  <input
                    type={currentQuestion.allowMultiple ? "checkbox" : "radio"}
                    name="correctAnswer"
                    checked={currentQuestion.allowMultiple 
                      ? Array.isArray(currentQuestion.correctAnswer) && currentQuestion.correctAnswer.includes(index)
                      : currentQuestion.correctAnswer === index
                    }
                    onChange={(e) => {
                      if (currentQuestion.allowMultiple) {
                        const currentAnswers = Array.isArray(currentQuestion.correctAnswer) ? currentQuestion.correctAnswer : [];
                        const newAnswers = e.target.checked
                          ? [...currentAnswers, index]
                          : currentAnswers.filter(i => i !== index);
                        setCurrentQuestion(prev => ({ ...prev, correctAnswer: newAnswers }));
                      } else {
                        setCurrentQuestion(prev => ({ ...prev, correctAnswer: index }));
                      }
                    }}
                    className="mr-2"
                  />
                  <span className="text-sm text-gray-600">Correct Answer</span>
                </div>
                
                <RichTextEditorWithImages
                  value={option || ''}
                  onChange={(value) => {
                    const newOptions = [...currentQuestion.options];
                    newOptions[index] = value;
                    setCurrentQuestion(prev => ({ ...prev, options: newOptions }));
                  }}
                  placeholder={`Option ${index + 1} text. You can type text and insert images inline. Drag images to reposition them.`}
                  label=""
                  rows={2}
                  showPreview={true}
                  showToolbar={true}
                  onImageUpload={async (file, imageId) => {
                    // This will be called when an image is uploaded in the editor
                    // The image will be uploaded during save, so we just return the blob URL for now
                    return { url: URL.createObjectURL(file) };
                  }}
                />
              </div>
            ))}
          </div>
        )}

        {/* Correct Answer Input for Integer and Floating Point Questions */}
        {(currentQuestion.type === 'integer' || currentQuestion.type === 'floating-point') && (
          <div className="mb-4">
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Correct Answer
            </label>
            <input
              type="number"
              step={currentQuestion.type === 'floating-point' ? 'any' : '1'}
              value={currentQuestion.correctAnswer || ''}
              onChange={(e) => {
                const value = e.target.value;
                setCurrentQuestion(prev => ({ 
                  ...prev, 
                  correctAnswer: currentQuestion.type === 'integer' 
                    ? (value === '' ? '' : parseInt(value) || 0) 
                    : (value === '' ? '' : parseFloat(value) || 0)
                }));
              }}
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
              placeholder={currentQuestion.type === 'integer' ? 'Enter integer answer' : 'Enter decimal answer'}
            />
            <p className="text-xs text-gray-500 mt-1">
              {currentQuestion.type === 'integer' 
                ? 'Enter the correct integer value (e.g., 5, -3, 0)' 
                : 'Enter the correct decimal value (e.g., 3.14, -2.5, 0.0)'
              }
            </p>
          </div>
        )}

        <div className="mb-4">
          <RichTextEditorWithImages
            value={currentQuestion.explanation || ''}
            onChange={(value) => setCurrentQuestion(prev => ({ ...prev, explanation: value }))}
            placeholder="Explain the correct answer... You can type text and insert images inline. Drag images to reposition them."
            label="Explanation"
            rows={3}
            showPreview={true}
            showToolbar={true}
            onImageUpload={async (file, imageId) => {
              // This will be called when an image is uploaded in the editor
              // The image will be uploaded during save, so we just return the blob URL for now
              return { url: URL.createObjectURL(file) };
            }}
          />
        </div>

        <div className="mb-4">
          <label className="block text-sm font-medium text-gray-700 mb-2">
            Marking Scheme
          </label>
          <div className="grid grid-cols-3 gap-4">
            <div>
              <label className="block text-xs text-gray-600 mb-1">Correct</label>
              <input
                type="number"
                step="0.1"
                value={currentQuestion.markingScheme.correct}
                onChange={(e) => updateMarkingScheme('correct', e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
              />
            </div>
            <div>
              <label className="block text-xs text-gray-600 mb-1">Incorrect</label>
              <input
                type="number"
                step="0.1"
                value={currentQuestion.markingScheme.incorrect}
                onChange={(e) => updateMarkingScheme('incorrect', e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
              />
            </div>
            <div>
              <label className="block text-xs text-gray-600 mb-1">Partial</label>
              <input
                type="number"
                step="0.1"
                value={currentQuestion.markingScheme.partial}
                onChange={(e) => updateMarkingScheme('partial', e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
              />
            </div>
          </div>
        </div>

        <div className="flex justify-end space-x-2">
          {editingQuestionIndex !== null ? (
            <>
              <button
                onClick={() => {
                  // cancel edit
                  setEditingQuestionIndex(null);
                  // Use test-level marking scheme as default
                  const resetMarkingScheme = testData.markingScheme || { correct: 4, incorrect: -1, partial: 0 };
                  setCurrentQuestion({
                    id: '',
                    type: 'multiple-choice',
                    question: '',
                    questionImage: null,
                    questionImageUrl: '',
                    options: ['', '', '', ''],
                    optionImages: [null, null, null, null],
                    optionImageUrls: ['', '', '', ''],
                    correctAnswer: 0,
                    explanation: '',
                    difficulty: 'Medium',
                    topic: '',
                    allowMultiple: false,
                    markingScheme: { ...resetMarkingScheme }
                  });
                }}
                className="px-4 py-2 border border-gray-300 text-gray-700 rounded-md hover:bg-gray-50"
              >
                Cancel Edit
              </button>
              <button
                onClick={handleAddQuestion}
                className="px-4 py-2 bg-green-600 text-white rounded-md hover:bg-green-700 flex items-center"
              >
                <Save size={16} className="mr-2" />
                Save Changes
              </button>
            </>
          ) : (
            <button
              onClick={handleAddQuestion}
              className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 flex items-center"
            >
              <Plus size={16} className="mr-2" />
              Add Question
            </button>
          )}
        </div>
      </div>
    );
  };

  const renderTestForm = () => {
    // Safety check: ensure testData exists
    if (!testData) {
      return (
        <div className="bg-white rounded-lg shadow-md p-6 mb-6">
          <p className="text-gray-500">Loading test data...</p>
        </div>
      );
    }
    
    return (
      <div className="bg-white rounded-lg shadow-md p-6 mb-6">
        <h3 className="text-lg font-semibold mb-4">Test Details</h3>
        
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
          {/* Test ID Field - Show when editing or creating with specific ID */}
          {(editingTest || showCreateForm) && (
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Test ID {editingTest && <span className="text-red-500">*</span>}
              </label>
              <input
                type="text"
                value={testData?.id || editingTestId || ''}
                onChange={(e) => {
                  const newId = e.target.value;
                  setTestData(prev => ({ ...prev, id: newId }));
                  setEditingTestId(newId);
                }}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                placeholder="Enter test ID (e.g., jeemain1)"
                required={editingTest}
              />
              <p className="text-xs text-gray-500 mt-1">
                {editingTest 
                  ? "This ID must match the document ID in Firestore"
                  : "Leave empty for auto-generated ID, or enter specific ID"
                }
              </p>
            </div>
          )}
          
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Test Title
            </label>
            <input
              type="text"
              value={testData?.title || ''}
              onChange={(e) => setTestData(prev => ({ ...prev, title: e.target.value }))}
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
              placeholder="Enter test title"
            />
          </div>
          <div className="flex items-end justify-end">
            <div className="flex items-center space-x-3">

              {/* PDF Import */}
              <input
                type="file"
                accept="application/pdf"
                onChange={(e) => importFromPdf(e.target.files && e.target.files[0])}
                className="hidden"
                id="pdf-import"
              />
              <label htmlFor="pdf-import" className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 cursor-pointer text-sm inline-flex items-center transition-colors">
                <FileText size={16} className="mr-2" />
                Import PDF
              </label>

              <input
                type="file"
                accept="application/pdf"
                onChange={(e) => importPdfWithMathpix(e.target.files && e.target.files[0])}
                className="hidden"
                id="mathpix-pdf-import"
              />
              <label htmlFor="mathpix-pdf-import" className={`px-4 py-2 bg-purple-600 text-white rounded-md hover:bg-purple-700 cursor-pointer text-sm inline-flex items-center transition-colors ${mathpixPdfLoading ? 'opacity-70 cursor-not-allowed' : ''}`}>
                <GitMerge size={16} className="mr-2" />
                {mathpixPdfLoading ? 'Parsing with Mathpix...' : 'Import PDF with Mathpix'}
              </label>

              {loading && (
                <div className="flex items-center text-sm text-gray-600">
                  <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-purple-600 mr-2"></div>
                  Parsing questions...
                </div>
              )}
              {mathpixPdfLoading && (
                <div className="flex items-center text-sm text-purple-700">
                  <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-purple-600 mr-2"></div>
                  Sending pages to Mathpix...
                </div>
              )}
            </div>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Exam Type
            </label>
            <select
              value={testData?.exam || (exams && exams.length > 0 ? exams[0] : 'CAT')}
              onChange={(e) => setTestData(prev => ({ 
                ...prev, 
                exam: e.target.value,
                // Auto-set interface type based on exam
                interfaceType: e.target.value.toLowerCase() === 'cat' ? 'cat' : 'jee'
              }))}
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
            >
              {exams && Array.isArray(exams) && exams.length > 0 ? exams.map(exam => (
                <option key={exam} value={exam}>{exam}</option>
              )) : <option value="CAT">CAT</option>}
            </select>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Test Interface Type
            </label>
            <select
              value={testData?.interfaceType || 'jee'}
              onChange={(e) => setTestData(prev => ({ ...prev, interfaceType: e.target.value }))}
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
            >
              <option value="jee">JEE Exam Interface</option>
              <option value="cat">CAT Exam Interface</option>
            </select>
            <p className="mt-1 text-xs text-gray-500">
              Select the interface style for the test instructions page
            </p>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Duration (minutes)
            </label>
            <input
              type="number"
              value={testData?.duration || ''}
              onChange={(e) => setTestData(prev => ({ ...prev, duration: e.target.value }))}
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
              placeholder="Enter duration in minutes"
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Total Questions
            </label>
            <input
              type="number"
              value={testData?.totalQuestions || ''}
              onChange={(e) => setTestData(prev => ({ ...prev, totalQuestions: e.target.value }))}
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
              placeholder="Enter total number of questions"
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Difficulty
            </label>
            <select
              value={testData?.difficulty || 'Medium'}
              onChange={(e) => setTestData(prev => ({ ...prev, difficulty: e.target.value }))}
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
            >
              {difficulties.map(difficulty => (
                <option key={difficulty} value={difficulty}>{difficulty}</option>
              ))}
            </select>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Subject
            </label>
            <input
              type="text"
              value={testData?.subject || ''}
              onChange={(e) => setTestData(prev => ({ ...prev, subject: e.target.value }))}
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
              placeholder="Enter subject (e.g., Mathematics, Physics, Chemistry)"
            />
            <p className="text-xs text-gray-500 mt-1">
              This will be used for organizing questions in revision and analytics
            </p>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Status
            </label>
            <select
              value={testData?.status || 'published'}
              onChange={(e) => setTestData(prev => ({ ...prev, status: e.target.value }))}
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
            >
              {statuses.map(status => (
                <option key={status} value={status}>{status.charAt(0).toUpperCase() + status.slice(1)}</option>
              ))}
            </select>
          </div>
        </div>

        <div className="mb-4">
          <label className="block text-sm font-medium text-gray-700 mb-2">
            Description
          </label>
          <textarea
            value={testData?.description || ''}
            onChange={(e) => setTestData(prev => ({ ...prev, description: e.target.value }))}
            rows={3}
            className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
            placeholder="Enter test description"
          />
        </div>

        {/* Default Marking Scheme */}
        <div className="mb-4 p-4 bg-green-50 dark:bg-green-900/20 border border-green-200 dark:border-green-700 rounded-lg">
          <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-3 flex items-center">
            <span className="mr-2">📝</span>
            Default Marking Scheme
          </label>
          <p className="text-xs text-gray-600 dark:text-gray-400 mb-3">
            This marking scheme will be applied to all questions by default. Individual questions can override this if needed.
          </p>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <div>
              <label className="block text-xs font-medium text-gray-700 dark:text-gray-300 mb-1">
                Correct Answer
              </label>
              <input
                type="number"
                step="0.1"
                value={testData?.markingScheme?.correct ?? 4}
                onChange={(e) => setTestData(prev => ({
                  ...prev,
                  markingScheme: {
                    ...(prev.markingScheme || { correct: 4, incorrect: -1, partial: 0 }),
                    correct: parseFloat(e.target.value) || 0
                  }
                }))}
                className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md focus:outline-none focus:ring-2 focus:ring-green-500 dark:bg-gray-700 dark:text-white"
              />
            </div>
            <div>
              <label className="block text-xs font-medium text-gray-700 dark:text-gray-300 mb-1">
                Incorrect Answer
              </label>
              <input
                type="number"
                step="0.1"
                value={testData?.markingScheme?.incorrect ?? -1}
                onChange={(e) => setTestData(prev => ({
                  ...prev,
                  markingScheme: {
                    ...(prev.markingScheme || { correct: 4, incorrect: -1, partial: 0 }),
                    incorrect: parseFloat(e.target.value) || 0
                  }
                }))}
                className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md focus:outline-none focus:ring-2 focus:ring-green-500 dark:bg-gray-700 dark:text-white"
              />
            </div>
            <div>
              <label className="block text-xs font-medium text-gray-700 dark:text-gray-300 mb-1">
                Partial Credit
              </label>
              <input
                type="number"
                step="0.1"
                value={testData?.markingScheme?.partial ?? 0}
                onChange={(e) => setTestData(prev => ({
                  ...prev,
                  markingScheme: {
                    ...(prev.markingScheme || { correct: 4, incorrect: -1, partial: 0 }),
                    partial: parseFloat(e.target.value) || 0
                  }
                }))}
                className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md focus:outline-none focus:ring-2 focus:ring-green-500 dark:bg-gray-700 dark:text-white"
              />
            </div>
          </div>
        </div>

        <div className="mb-4 p-3 bg-blue-50 border border-blue-200 rounded-md">
          <p className="text-sm text-blue-800">
            <strong>Note:</strong> Only tests with "Published" status will appear in the mock test section for users. 
            Draft tests are only visible in the admin panel.
          </p>
        </div>

        {/* Test Interface Features Configuration */}
        <div className="mb-4 p-4 bg-gray-50 dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-lg">
          <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-3 flex items-center">
            <Settings size={18} className="mr-2 text-gray-600 dark:text-gray-400" />
            Test Interface Features
          </label>
          <p className="text-xs text-gray-600 dark:text-gray-400 mb-3">
            Enable or disable specific features for this test. These settings control what tools are available to students during the test.
          </p>
          
          <div className="space-y-3">
            {/* Language Switching */}
            <div className="flex items-center justify-between p-3 bg-white dark:bg-gray-700 rounded border border-gray-200 dark:border-gray-600">
              <div className="flex-1">
                <label className="text-sm font-medium text-gray-700 dark:text-gray-300 cursor-pointer">
                  Language Switching
                </label>
                <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">
                  Allow students to switch between English and Hindi during the test
                </p>
              </div>
              <label className="relative inline-flex items-center cursor-pointer">
                <input
                  type="checkbox"
                  checked={testData?.enableLanguageSwitch || false}
                  onChange={(e) => setTestData(prev => ({ ...prev, enableLanguageSwitch: e.target.checked }))}
                  className="sr-only peer"
                />
                <div className="w-11 h-6 bg-gray-200 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-blue-300 dark:peer-focus:ring-blue-800 rounded-full peer dark:bg-gray-600 peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all dark:border-gray-600 peer-checked:bg-blue-600"></div>
              </label>
            </div>

            {/* Calculator */}
            <div className="flex items-center justify-between p-3 bg-white dark:bg-gray-700 rounded border border-gray-200 dark:border-gray-600">
              <div className="flex-1">
                <label className="text-sm font-medium text-gray-700 dark:text-gray-300 cursor-pointer">
                  On-Screen Calculator
                </label>
                <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">
                  Enable an on-screen calculator tool for students during the test
                </p>
              </div>
              <label className="relative inline-flex items-center cursor-pointer">
                <input
                  type="checkbox"
                  checked={testData?.enableCalculator || false}
                  onChange={(e) => setTestData(prev => ({ ...prev, enableCalculator: e.target.checked }))}
                  className="sr-only peer"
                />
                <div className="w-11 h-6 bg-gray-200 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-blue-300 dark:peer-focus:ring-blue-800 rounded-full peer dark:bg-gray-600 peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all dark:border-gray-600 peer-checked:bg-blue-600"></div>
              </label>
            </div>

            {/* Question Paper View */}
            <div className="flex items-center justify-between p-3 bg-white dark:bg-gray-700 rounded border border-gray-200 dark:border-gray-600">
              <div className="flex-1">
                <label className="text-sm font-medium text-gray-700 dark:text-gray-300 cursor-pointer">
                  View Question Paper Button
                </label>
                <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">
                  Allow students to view the complete question paper during the test
                </p>
              </div>
              <label className="relative inline-flex items-center cursor-pointer">
                <input
                  type="checkbox"
                  checked={testData?.enableQuestionPaper || false}
                  onChange={(e) => setTestData(prev => ({ ...prev, enableQuestionPaper: e.target.checked }))}
                  className="sr-only peer"
                />
                <div className="w-11 h-6 bg-gray-200 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-blue-300 dark:peer-focus:ring-blue-800 rounded-full peer dark:bg-gray-600 peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all dark:border-gray-600 peer-checked:bg-blue-600"></div>
              </label>
            </div>
          </div>
        </div>

        {/* Manual Evaluation and Live Test Configuration */}
        <div className="mb-4 p-4 bg-yellow-50 dark:bg-yellow-900/20 border border-yellow-200 dark:border-yellow-700 rounded-lg">
          <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-3 flex items-center">
            <Settings size={18} className="mr-2 text-yellow-600 dark:text-yellow-400" />
            Manual Evaluation & Live Test Settings
          </label>
          <p className="text-xs text-gray-600 dark:text-gray-400 mb-3">
            Configure manual evaluation for subjective papers and live test timing.
          </p>
          
          <div className="space-y-3">
            {/* Manual Evaluation Toggle */}
            <div className="flex items-center justify-between p-3 bg-white dark:bg-gray-700 rounded border border-gray-200 dark:border-gray-600">
              <div className="flex-1">
                <label className="text-sm font-medium text-gray-700 dark:text-gray-300 cursor-pointer">
                  Manual Evaluation Mode
                </label>
                <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">
                  Enable for subjective papers. Answers will be saved for admin evaluation. No auto-checking.
                </p>
              </div>
              <label className="relative inline-flex items-center cursor-pointer">
                <input
                  type="checkbox"
                  checked={testData?.manualEvaluation || false}
                  onChange={(e) => setTestData(prev => ({ ...prev, manualEvaluation: e.target.checked }))}
                  className="sr-only peer"
                />
                <div className="w-11 h-6 bg-gray-200 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-yellow-300 dark:peer-focus:ring-yellow-800 rounded-full peer dark:bg-gray-600 peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all dark:border-gray-600 peer-checked:bg-yellow-600"></div>
              </label>
            </div>

            {/* Live Test Toggle */}
            <div className="flex items-center justify-between p-3 bg-white dark:bg-gray-700 rounded border border-gray-200 dark:border-gray-600">
              <div className="flex-1">
                <label className="text-sm font-medium text-gray-700 dark:text-gray-300 cursor-pointer">
                  Live Test Mode
                </label>
                <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">
                  Enable to set fixed start/end time. Test will be locked after end time.
                </p>
              </div>
              <label className="relative inline-flex items-center cursor-pointer">
                <input
                  type="checkbox"
                  checked={testData?.isLiveTest || false}
                  onChange={(e) => {
                    setTestData(prev => {
                      const newData = { ...prev, isLiveTest: e.target.checked };
                      // If disabling live test, clear start/end times
                      if (!e.target.checked) {
                        newData.startTime = null;
                        newData.endTime = null;
                      }
                      return newData;
                    });
                  }}
                  className="sr-only peer"
                />
                <div className="w-11 h-6 bg-gray-200 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-yellow-300 dark:peer-focus:ring-yellow-800 rounded-full peer dark:bg-gray-600 peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all dark:border-gray-600 peer-checked:bg-yellow-600"></div>
              </label>
            </div>

            {/* Start Time Input (only shown when live test is enabled) */}
            {testData?.isLiveTest && (
              <div className="p-3 bg-white dark:bg-gray-700 rounded border border-gray-200 dark:border-gray-600">
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                  Test Start Time
                </label>
                <input
                  type="datetime-local"
                  value={testData?.startTime ? (() => {
                    // Convert ISO string to local datetime-local format
                    const date = new Date(testData.startTime);
                    const year = date.getFullYear();
                    const month = String(date.getMonth() + 1).padStart(2, '0');
                    const day = String(date.getDate()).padStart(2, '0');
                    const hours = String(date.getHours()).padStart(2, '0');
                    const minutes = String(date.getMinutes()).padStart(2, '0');
                    return `${year}-${month}-${day}T${hours}:${minutes}`;
                  })() : ''}
                  onChange={(e) => {
                    if (e.target.value) {
                      // datetime-local gives us local time string like "2024-01-15T14:00"
                      // We need to create a Date object that represents this local time
                      // and store it as ISO string (which will include timezone info)
                      const localDateTime = e.target.value; // Format: "YYYY-MM-DDTHH:mm"
                      const date = new Date(localDateTime);
                      
                      // Check if date is valid
                      if (isNaN(date.getTime())) {
                        return;
                      }
                      
                      // Store as ISO string (this preserves the local time intent)
                      const startTime = date.toISOString();
                      const duration = parseInt(testData?.duration) || 0;
                      const endTime = duration ? new Date(date.getTime() + duration * 60 * 1000).toISOString() : null;
                      setTestData(prev => ({ ...prev, startTime, endTime }));
                    } else {
                      setTestData(prev => ({ ...prev, startTime: null, endTime: null }));
                    }
                  }}
                  className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md focus:outline-none focus:ring-2 focus:ring-yellow-500 dark:bg-gray-800 dark:text-white"
                />
                <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">
                  End time will be automatically calculated: Start Time + Duration
                </p>
                {testData?.startTime && (
                  <p className="text-xs text-gray-600 dark:text-gray-400 mt-1">
                    Start Time: {new Date(testData.startTime).toLocaleString()}
                  </p>
                )}
                {testData?.endTime && (
                  <p className="text-xs text-yellow-600 dark:text-yellow-400 mt-1">
                    End Time: {new Date(testData.endTime).toLocaleString()}
                  </p>
                )}
              </div>
            )}
          </div>
        </div>

        {/* Analysis Video Upload Section */}
        <div className="mb-4 p-4 bg-purple-50 dark:bg-purple-900/20 border border-purple-200 dark:border-purple-700 rounded-lg">
          <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-3 flex items-center">
            <Video size={18} className="mr-2 text-purple-600 dark:text-purple-400" />
            Analysis Video by 99 Percentiler (Optional)
          </label>
          <p className="text-xs text-gray-600 dark:text-gray-400 mb-3">
            Upload a video analysis or provide a video URL (YouTube, Vimeo, or direct video link). 
            This video will be displayed to students in their test results.
          </p>
          
          {/* Video URL Input */}
          <div className="mb-3">
            <label className="block text-xs font-medium text-gray-600 dark:text-gray-400 mb-1">
              Video URL (YouTube, Vimeo, or direct link)
            </label>
            <input
              type="url"
              value={testData?.analysisVideoUrl || ''}
              onChange={(e) => setTestData(prev => ({ ...prev, analysisVideoUrl: e.target.value }))}
              className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md focus:outline-none focus:ring-2 focus:ring-purple-500 dark:bg-gray-700 dark:text-white text-sm"
              placeholder="https://youtube.com/watch?v=... or https://vimeo.com/... or direct video URL"
            />
          </div>

          {/* Video File Upload */}
          <div className="border-2 border-dashed border-purple-300 dark:border-purple-600 rounded-lg p-4">
            <div className="text-center">
              <input
                type="file"
                accept="video/*"
                onChange={async (e) => {
                  const file = e.target.files[0];
                  if (file) {
                    // Validate file size (max 500MB)
                    const maxSize = 500 * 1024 * 1024; // 500MB
                    if (file.size > maxSize) {
                      setError('Video file is too large. Maximum size is 500MB.');
                      return;
                    }

                    // Validate file type
                    if (!file.type.startsWith('video/')) {
                      setError('Please select a valid video file.');
                      return;
                    }

                    setTestData(prev => ({ ...prev, analysisVideoFile: file }));
                    
                    // Upload video to Firebase Storage
                    try {
                      setLoading(true);
                      const fileName = `analysis-videos/${testData.id || `test-${Date.now()}`}/${Date.now()}_${file.name}`;
                      const uploadResult = await uploadFile(file, fileName);
                      
                      if (uploadResult.success) {
                        setTestData(prev => ({ 
                          ...prev, 
                          analysisVideoUrl: uploadResult.url,
                          analysisVideoFile: null 
                        }));
                        setSuccess('Video uploaded successfully!');
                      } else {
                        setError(`Failed to upload video: ${uploadResult.error || 'Unknown error'}`);
                        setTestData(prev => ({ ...prev, analysisVideoFile: null }));
                      }
                    } catch (error) {
                      setError(`Error uploading video: ${error.message}`);
                      setTestData(prev => ({ ...prev, analysisVideoFile: null }));
                    } finally {
                      setLoading(false);
                    }
                  }
                }}
                className="hidden"
                id="analysis-video-upload"
              />
              <label
                htmlFor="analysis-video-upload"
                className="cursor-pointer flex flex-col items-center"
              >
                <Upload size={24} className="text-purple-600 dark:text-purple-400 mb-2" />
                <span className="text-sm text-gray-700 dark:text-gray-300 font-medium mb-1">
                  {testData?.analysisVideoFile ? 'Uploading...' : 'Upload Video File'}
                </span>
                <span className="text-xs text-gray-500 dark:text-gray-400">
                  MP4, WebM, MOV (Max 500MB)
                </span>
              </label>
            </div>
            
            {testData?.analysisVideoUrl && (
              <div className="mt-3 p-3 bg-white dark:bg-gray-800 rounded border border-purple-200 dark:border-purple-700">
                <div className="flex items-center justify-between">
                  <div className="flex items-center space-x-2">
                    <Video size={16} className="text-purple-600 dark:text-purple-400" />
                    <span className="text-sm text-gray-700 dark:text-gray-300">
                      Video URL set
                    </span>
                  </div>
                  <button
                    type="button"
                    onClick={() => setTestData(prev => ({ 
                      ...prev, 
                      analysisVideoUrl: '',
                      analysisVideoFile: null 
                    }))}
                    className="text-xs text-red-600 hover:text-red-800 dark:text-red-400 dark:hover:text-red-300"
                  >
                    Remove
                  </button>
                </div>
                <a
                  href={testData.analysisVideoUrl}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="text-xs text-purple-600 dark:text-purple-400 hover:underline mt-1 block truncate"
                >
                  {testData.analysisVideoUrl}
                </a>
              </div>
            )}
          </div>
        </div>
      </div>
    );
  };

  const renderQuestionsList = () => {
    return (
      <div className="bg-white rounded-lg shadow-md p-6 mb-6">
        <h3 className="text-lg font-semibold mb-4">
          Questions ({testData.questions?.length || 0})
          {(testData.questions?.length || 0) > 1 && (
            <span className="text-sm text-gray-500 ml-2">
              • Drag questions to reorder
            </span>
          )}
        </h3>
        
        {!testData.questions || testData.questions.length === 0 ? (
          <p className="text-gray-500 text-center py-8">No questions added yet.</p>
        ) : (
          <DndContext
            sensors={sensors}
            collisionDetection={closestCenter}
            onDragEnd={handleDragEnd}
          >
            <SortableContext
              items={(testData.questions || []).map((q, idx) => q?.id || `q-temp-${idx}`)}
              strategy={verticalListSortingStrategy}
            >
              <div className="space-y-4">
                {(testData.questions || []).map((question, index) => (
                  <SortableQuestionItem
                    key={question?.id || `q-${index}-${question?.question?.substring(0, 10) || index}`}
                    question={question}
                    index={index}
                    onEdit={handleEditQuestion}
                    onRemove={handleRemoveQuestion}
                  />
                ))}
              </div>
            </SortableContext>
          </DndContext>
        )}
      </div>
    );
  };

  if (showCreateForm) {
    // Safety check: ensure testData exists, if not show loading
    if (!testData) {
      return (
        <div className="space-y-6">
          <p className="text-gray-500">Loading form...</p>
        </div>
      );
    }
    
    return (
      <div className="space-y-6">
        <div className="flex items-center justify-between">
          <h1 className="text-2xl font-bold text-gray-900 dark:text-white">
            {editingTest ? 'Edit Test' : 'Create New Test'}
          </h1>
          <button
            onClick={() => setShowCreateForm(false)}
            className="text-gray-600 hover:text-gray-800"
          >
            <X size={24} />
          </button>
        </div>

        {(error || success) && (
          <div className="mb-2">
            {error && (
              <div className="p-3 rounded-md bg-red-50 text-red-700 border border-red-200 text-sm">
                {error}
              </div>
            )}
            {success && (
              <div className="p-3 rounded-md bg-green-50 text-green-700 border border-green-200 text-sm mt-2">
                {success}
              </div>
            )}
          </div>
        )}

        {renderTestForm()}
        
        {/* Dynamic Section Configuration */}
        <div className="bg-white rounded-lg shadow-md p-6 mb-6">
          <div className="flex items-center justify-between mb-4">
            <h3 className="text-lg font-semibold text-gray-900 dark:text-white flex items-center">
              <Settings size={16} className="mr-2" />
              Test Sections Configuration
            </h3>
            <div className="flex items-center space-x-2">
              <label className="flex items-center">
                <input
                  type="checkbox"
                  checked={testData.useDynamicSections}
                  onChange={toggleDynamicSections}
                  className="mr-2"
                />
                <span className="text-sm text-gray-700 dark:text-gray-300">
                  Enable Dynamic Sections
                </span>
              </label>
            </div>
          </div>
          
          {testData.useDynamicSections ? (
            <DynamicSectionConfig
              sections={testData.sections || []}
              totalQuestions={testData.totalQuestions || 0}
              onSectionsChange={handleSectionsChange}
              onValidationChange={handleSectionValidationChange}
            />
          ) : (
            <div className="text-center py-8 text-gray-500">
              <Settings size={24} className="mx-auto mb-2" />
              <p>Enable dynamic sections to configure test sections with question ranges</p>
            </div>
          )}
        </div>
        
        {renderQuestionForm()}
        {renderQuestionsList()}

        <div className="flex justify-end space-x-2">
          <button
            onClick={() => setShowCreateForm(false)}
            className="px-4 py-2 border border-gray-300 text-gray-700 rounded-md hover:bg-gray-50"
          >
            Cancel
          </button>
          <button
            onClick={handleSaveTest}
            disabled={saving}
            className={`px-4 py-2 rounded-md flex items-center text-white ${saving ? 'bg-green-400 cursor-not-allowed' : 'bg-green-600 hover:bg-green-700'}`}
          >
            <Save size={16} className="mr-2" />
            {saving ? (editingTest ? 'Updating...' : 'Saving...') : (editingTest ? 'Update Test' : 'Save Test')}
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between">
        <div>
          <h1 className="text-2xl font-bold text-gray-900 dark:text-white">
            Test Management
          </h1>
          <p className="text-gray-600 dark:text-gray-400 mt-1">
            Create, edit, and manage mock tests with advanced question types
          </p>
        </div>
        <div className="mt-4 sm:mt-0 flex flex-col sm:flex-row gap-2">
          <button
            onClick={handleCreateTest}
            className="btn-primary inline-flex items-center"
          >
            <Plus size={16} className="mr-2" />
            Create Test
          </button>
          <button
            onClick={() => {
              if (isMergeUnlocked) {
                setShowMergeModal(true);
                setSelectedTests([]);
                setSelectedQuestions(new Set());
                setMergeTestData({
                  id: '',
                  title: '',
                  description: '',
                  duration: '',
                  exam: 'CAT',
                  difficulty: 'Medium',
                  status: 'draft',
                  subject: 'General',
                  markingScheme: {
                    correct: 4,
                    incorrect: -1,
                    partial: 0
                  },
                  useDynamicSections: false,
                  sections: []
                });
              } else {
                setShowPasswordPrompt(true);
                setMergePassword('');
              }
            }}
            className="btn-secondary inline-flex items-center"
          >
            {isMergeUnlocked ? (
              <>
                <Unlock size={16} className="mr-2" />
                Merge Tests
              </>
            ) : (
              <>
                <Lock size={16} className="mr-2" />
                Merge Tests
              </>
            )}
          </button>
        </div>
      </div>

      {/* Search and Filter Bar */}
      {!loading && tests.length > 0 && (
        <div className="bg-white dark:bg-gray-800 rounded-lg shadow-md p-4">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            {/* Search Bar */}
            <div className="relative">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-5 h-5" />
              <input
                type="text"
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                placeholder="Search tests by title or description..."
                className="w-full pl-10 pr-4 py-2 border border-gray-300 dark:border-gray-600 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 dark:bg-gray-700 dark:text-white"
              />
              {searchTerm && (
                <button
                  onClick={() => setSearchTerm('')}
                  className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-400 hover:text-gray-600 dark:hover:text-gray-300"
                >
                  <X className="w-4 h-4" />
                </button>
              )}
            </div>

            {/* Exam Filter */}
            <div>
              <select
                value={filterExam}
                onChange={(e) => setFilterExam(e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 dark:bg-gray-700 dark:text-white"
              >
                <option value="">All Exams</option>
                {exams.map(exam => (
                  <option key={exam} value={exam}>{exam}</option>
                ))}
              </select>
            </div>

            {/* Status Filter */}
            <div>
              <select
                value={filterStatus}
                onChange={(e) => setFilterStatus(e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 dark:bg-gray-700 dark:text-white"
              >
                <option value="">All Status</option>
                {statuses.map(status => (
                  <option key={status} value={status}>
                    {status.charAt(0).toUpperCase() + status.slice(1)}
                  </option>
                ))}
              </select>
            </div>
          </div>

          {/* Results Count */}
          {filteredTests.length !== tests.length && (
            <div className="mt-3 text-sm text-gray-600 dark:text-gray-400">
              Showing {filteredTests.length} of {tests.length} test{tests.length !== 1 ? 's' : ''}
              {(searchTerm || filterExam || filterStatus) && (
                <button
                  onClick={() => {
                    setSearchTerm('');
                    setFilterExam('');
                    setFilterStatus('');
                  }}
                  className="ml-2 text-blue-600 dark:text-blue-400 hover:underline"
                >
                  Clear filters
                </button>
              )}
            </div>
          )}
        </div>
      )}

      {loading ? (
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="card p-8 text-center"
        >
          <FileText className="mx-auto h-12 w-12 text-gray-400 mb-4" />
          <h3 className="text-lg font-medium text-gray-900 dark:text-white mb-2">
            Loading Tests...
          </h3>
          <p className="text-gray-600 dark:text-gray-400 mb-6">
            Please wait while we fetch the available tests.
          </p>
        </motion.div>
      ) : error ? (
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="card p-8 text-center"
        >
          <AlertCircle className="mx-auto h-12 w-12 text-red-400 mb-4" />
          <h3 className="text-lg font-medium text-gray-900 dark:text-white mb-2">
            Error: {error}
          </h3>
          <p className="text-gray-600 dark:text-gray-400 mb-6">
            Failed to load tests. Please try again later.
          </p>
          <button
            onClick={loadTests}
            className="btn-primary inline-flex items-center"
          >
            <Database size={16} className="mr-2" />
            Retry Loading Tests
          </button>
        </motion.div>
      ) : tests.length === 0 ? (
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="card p-8 text-center"
        >
          <FileText className="mx-auto h-12 w-12 text-gray-400 mb-4" />
          <h3 className="text-lg font-medium text-gray-900 dark:text-white mb-2">
            No Tests Created Yet
          </h3>
          <p className="text-gray-600 dark:text-gray-400 mb-6">
            Create your first mock test with advanced question types and marking schemes.
          </p>
          <button
            onClick={handleCreateTest}
            className="btn-primary inline-flex items-center"
          >
            <Plus size={16} className="mr-2" />
            Create New Test
          </button>
        </motion.div>
      ) : (
        <div className="grid gap-6">
          {filteredTests.map((test, index) => (
            <motion.div
              key={test.id || `test-${index}-${test.title?.substring(0, 10) || index}`}
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: index * 0.1 }}
              className="card p-6"
            >
              <div className="flex items-center justify-between mb-4">
                <div>
                  <h3 className="text-lg font-semibold text-gray-900 dark:text-white">
                    {test.title}
                  </h3>
                  <p className="text-gray-600 dark:text-gray-400">
                    {test.exam} • {(test.questions || []).length} questions • {test.duration} minutes
                  </p>
                  <div className="flex items-center space-x-2 mt-1">
                    <span className={`inline-flex items-center px-2 py-1 rounded-full text-xs font-medium ${
                      test.status === 'published' 
                        ? 'bg-green-100 text-green-800' 
                        : test.status === 'draft'
                        ? 'bg-yellow-100 text-yellow-800'
                        : 'bg-gray-100 text-gray-800'
                    }`}>
                      {test.status?.charAt(0).toUpperCase() + test.status?.slice(1) || 'Draft'}
                    </span>
                    <span className="bg-blue-100 text-blue-800 text-xs px-2 py-1 rounded">
                      {test.difficulty || 'Medium'}
                    </span>
                  </div>
                </div>
                <div className="flex space-x-2">
                  {test.status !== 'published' && (
                    <button 
                      onClick={() => handlePublishTest(test.id)}
                      className="btn-success inline-flex items-center"
                    >
                      <Eye size={16} className="mr-2" />
                      Publish
                    </button>
                  )}
                  <button 
                    onClick={() => handleEditTest(test)}
                    className="btn-secondary inline-flex items-center"
                  >
                    <Edit size={16} className="mr-2" />
                    Edit
                  </button>
                  <button 
                    onClick={() => handleDeleteTest(test.id)}
                    className="btn-danger inline-flex items-center"
                  >
                    <Trash2 size={16} className="mr-2" />
                    Delete
                  </button>
                </div>
              </div>
              
              <div className="flex flex-wrap gap-2">
                {test.questions && Array.isArray(test.questions) && test.questions.length > 0 ? (
                  test.questions.map((q, qIndex) => (
                    <span
                      key={q?.id || `q-tag-${test.id}-${qIndex}`}
                      className="bg-blue-100 text-blue-800 text-xs px-2 py-1 rounded"
                    >
                      {q.type}
                    </span>
                  ))
                ) : (
                  <span className="text-gray-500 text-sm">No questions</span>
                )}
              </div>
              
              {/* Manual Evaluation Responses Section */}
              {test.manualEvaluation && (
                <div className="mt-4 border-t pt-4">
                  <button
                    onClick={() => setEvaluationModalTest(test)}
                    className="flex items-center justify-between w-full px-4 py-2 bg-purple-50 dark:bg-purple-900/20 hover:bg-purple-100 dark:hover:bg-purple-900/30 rounded-lg transition-colors"
                  >
                    <span className="text-sm font-medium text-purple-700 dark:text-purple-300">
                      View Responses & Evaluate
                    </span>
                    <Eye className="w-4 h-4 text-purple-600 dark:text-purple-400" />
                  </button>
                </div>
              )}
            </motion.div>
          ))}
        </div>
      )}

      {/* Bulk Import/Export Section */}
      <div className="bg-white rounded-lg shadow-md p-6">
        <h3 className="text-lg font-semibold mb-4 flex items-center">
          <Database size={16} className="mr-2" /> Bulk Operations
        </h3>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div>
            <button 
              onClick={() => setShowBulkImport(true)}
              className="btn-primary inline-flex items-center"
            >
              <Upload size={16} className="mr-2" />
              Import Tests (CSV)
            </button>
            {showBulkImport && (
              <div className="mt-4 p-4 bg-gray-50 rounded-md">
                <textarea
                  value={bulkData}
                  onChange={(e) => setBulkData(e.target.value)}
                  rows={5}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                  placeholder="Enter CSV data (Title|Description|Exam|Difficulty|Duration|QuestionsJSON)"
                />
                <div className="flex justify-end space-x-2 mt-4">
                  <button
                    onClick={() => setShowBulkImport(false)}
                    className="px-4 py-2 border border-gray-300 text-gray-700 rounded-md hover:bg-gray-50"
                  >
                    Cancel
                  </button>
                  <button
                    onClick={handleBulkImport}
                    className="px-4 py-2 bg-green-600 text-white rounded-md hover:bg-green-700 flex items-center"
                  >
                    <Save size={16} className="mr-2" />
                    Import
                  </button>
                </div>
              </div>
            )}
          </div>
          <div>
            <button 
              onClick={exportTests}
              className="btn-secondary inline-flex items-center"
            >
              <Download size={16} className="mr-2" />
              Export Tests (CSV)
            </button>
          </div>
        </div>
        
      </div>


      {/* Merge Tests Modal */}
      {showMergeModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4 overflow-y-auto">
          <motion.div
            initial={{ opacity: 0, scale: 0.95 }}
            animate={{ opacity: 1, scale: 1 }}
            className="bg-white dark:bg-gray-800 rounded-lg shadow-xl max-w-6xl w-full max-h-[90vh] overflow-y-auto"
          >
            <div className="sticky top-0 bg-white dark:bg-gray-800 border-b border-gray-200 dark:border-gray-700 p-6 flex items-center justify-between z-10">
              <h2 className="text-2xl font-bold text-gray-900 dark:text-white">Create Test from Merged Questions</h2>
              <button
                onClick={() => setShowMergeModal(false)}
                className="text-gray-400 hover:text-gray-600 dark:hover:text-gray-300"
              >
                <X className="w-6 h-6" />
              </button>
            </div>

            <div className="p-6 space-y-6">
              {/* Test Details Section */}
              <div className="bg-gray-50 dark:bg-gray-700/50 rounded-lg p-6">
                <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-4">Test Details</h3>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                      Test ID
                    </label>
                    <input
                      type="text"
                      value={mergeTestData.id}
                      onChange={(e) => setMergeTestData(prev => ({ ...prev, id: e.target.value }))}
                      className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 dark:bg-gray-700 dark:text-white"
                      placeholder="Leave empty to auto-generate"
                    />
                    <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">
                      Optional: Leave empty to auto-generate a unique ID
                    </p>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                      Test Title *
                    </label>
                    <input
                      type="text"
                      value={mergeTestData.title}
                      onChange={(e) => setMergeTestData(prev => ({ ...prev, title: e.target.value }))}
                      className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 dark:bg-gray-700 dark:text-white"
                      placeholder="Enter test title"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                      Duration (minutes) *
                    </label>
                    <input
                      type="number"
                      value={mergeTestData.duration}
                      onChange={(e) => setMergeTestData(prev => ({ ...prev, duration: e.target.value }))}
                      className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 dark:bg-gray-700 dark:text-white"
                      placeholder="e.g., 90"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                      Exam
                    </label>
                    <select
                      value={mergeTestData.exam}
                      onChange={(e) => setMergeTestData(prev => ({ ...prev, exam: e.target.value }))}
                      className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 dark:bg-gray-700 dark:text-white"
                    >
                      {exams.map(exam => (
                        <option key={exam} value={exam}>{exam}</option>
                      ))}
                    </select>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                      Difficulty
                    </label>
                    <select
                      value={mergeTestData.difficulty}
                      onChange={(e) => setMergeTestData(prev => ({ ...prev, difficulty: e.target.value }))}
                      className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 dark:bg-gray-700 dark:text-white"
                    >
                      {difficulties.map(diff => (
                        <option key={diff} value={diff}>{diff}</option>
                      ))}
                    </select>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                      Description
                    </label>
                    <textarea
                      value={mergeTestData.description}
                      onChange={(e) => setMergeTestData(prev => ({ ...prev, description: e.target.value }))}
                      rows={2}
                      className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 dark:bg-gray-700 dark:text-white"
                      placeholder="Enter test description"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                      Status
                    </label>
                    <select
                      value={mergeTestData.status}
                      onChange={(e) => setMergeTestData(prev => ({ ...prev, status: e.target.value }))}
                      className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 dark:bg-gray-700 dark:text-white"
                    >
                      {statuses.map(status => (
                        <option key={status} value={status}>{status.charAt(0).toUpperCase() + status.slice(1)}</option>
                      ))}
                    </select>
                  </div>
                </div>

                {/* Marking Scheme */}
                <div className="mt-4 pt-4 border-t border-gray-200 dark:border-gray-600">
                  <h4 className="text-sm font-semibold text-gray-900 dark:text-white mb-3">Marking Scheme</h4>
                  <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                    <div>
                      <label className="block text-xs font-medium text-gray-700 dark:text-gray-300 mb-1">
                        Correct Answer
                      </label>
                      <input
                        type="number"
                        value={mergeTestData.markingScheme.correct}
                        onChange={(e) => setMergeTestData(prev => ({
                          ...prev,
                          markingScheme: { ...prev.markingScheme, correct: parseFloat(e.target.value) || 0 }
                        }))}
                        className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 dark:bg-gray-700 dark:text-white"
                      />
                    </div>
                    <div>
                      <label className="block text-xs font-medium text-gray-700 dark:text-gray-300 mb-1">
                        Incorrect Answer
                      </label>
                      <input
                        type="number"
                        value={mergeTestData.markingScheme.incorrect}
                        onChange={(e) => setMergeTestData(prev => ({
                          ...prev,
                          markingScheme: { ...prev.markingScheme, incorrect: parseFloat(e.target.value) || 0 }
                        }))}
                        className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 dark:bg-gray-700 dark:text-white"
                      />
                    </div>
                    <div>
                      <label className="block text-xs font-medium text-gray-700 dark:text-gray-300 mb-1">
                        Partial Credit
                      </label>
                      <input
                        type="number"
                        value={mergeTestData.markingScheme.partial}
                        onChange={(e) => setMergeTestData(prev => ({
                          ...prev,
                          markingScheme: { ...prev.markingScheme, partial: parseFloat(e.target.value) || 0 }
                        }))}
                        className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 dark:bg-gray-700 dark:text-white"
                      />
                    </div>
                  </div>
                </div>

                {/* Dynamic Sections Toggle */}
                <div className="mt-4 pt-4 border-t border-gray-200 dark:border-gray-600">
                  <div className="flex items-center justify-between">
                    <div>
                      <h4 className="text-sm font-semibold text-gray-900 dark:text-white">Dynamic Sections</h4>
                      <p className="text-xs text-gray-600 dark:text-gray-400">Configure sections for this test</p>
                    </div>
                    <button
                      onClick={() => {
                        setShowSectionConfig(true);
                        setIsSectionConfigValid(false);
                      }}
                      className="px-4 py-2 bg-blue-600 text-white text-sm rounded-md hover:bg-blue-700"
                    >
                      Configure Sections
                    </button>
                  </div>
                  {mergeTestData.useDynamicSections && mergeTestData.sections.length > 0 && (
                    <div className="mt-3 p-3 bg-blue-50 dark:bg-blue-900/20 rounded-md">
                      <p className="text-xs text-blue-800 dark:text-blue-300">
                        {mergeTestData.sections.length} section(s) configured
                      </p>
                    </div>
                  )}
                </div>
              </div>

              {/* Select Tests Section */}
              <div className="bg-gray-50 dark:bg-gray-700/50 rounded-lg p-6">
                <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-4">Step 1: Select Source Tests</h3>
                <div className="space-y-2 max-h-60 overflow-y-auto">
                  {tests.length === 0 ? (
                    <p className="text-gray-500 dark:text-gray-400 text-center py-4">No tests available</p>
                  ) : (
                    tests.map((test, testIdx) => (
                      <label
                        key={test.id || `test-select-${testIdx}-${test.title?.substring(0, 10) || testIdx}`}
                        className="flex items-center p-3 bg-white dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700 hover:border-blue-500 dark:hover:border-blue-500 cursor-pointer"
                      >
                        <input
                          type="checkbox"
                          checked={selectedTests.includes(test.id)}
                          onChange={(e) => {
                            if (e.target.checked) {
                              setSelectedTests([...selectedTests, test.id]);
                            } else {
                              setSelectedTests(selectedTests.filter(id => id !== test.id));
                              // Remove questions from this test when deselected
                              const testQuestions = test.questions || [];
                              const questionIds = testQuestions.map((q, idx) => `${test.id}-${idx}`);
                              setSelectedQuestions(prev => {
                                const newSet = new Set(prev);
                                questionIds.forEach(id => newSet.delete(id));
                                return newSet;
                              });
                            }
                          }}
                          className="w-4 h-4 text-blue-600 border-gray-300 rounded focus:ring-blue-500"
                        />
                        <div className="ml-3 flex-1">
                          <div className="font-medium text-gray-900 dark:text-white">{test.title}</div>
                          <div className="text-sm text-gray-600 dark:text-gray-400">
                            {test.exam} • {test.questions?.length || 0} questions • {test.duration} min
                          </div>
                        </div>
                      </label>
                    ))
                  )}
                </div>
              </div>

              {/* Live Preview Section */}
              {orderedPreviewQuestions.length > 0 && (
                <div className="bg-gray-50 dark:bg-gray-700/50 rounded-lg p-6">
                  <div className="flex items-center justify-between mb-4">
                    <h3 className="text-lg font-semibold text-gray-900 dark:text-white">
                      Step 3: Preview & Reorder Questions ({orderedPreviewQuestions.length} questions)
                    </h3>
                    <p className="text-sm text-gray-600 dark:text-gray-400">
                      Drag questions to reorder them
                    </p>
                  </div>
                  
                  <DndContext
                    sensors={sensors}
                    collisionDetection={closestCenter}
                    onDragEnd={(event) => {
                      const { active, over } = event;
                      if (over && active.id !== over.id) {
                        setOrderedPreviewQuestions((items) => {
                          const oldIndex = items.findIndex(item => item.id === active.id);
                          const newIndex = items.findIndex(item => item.id === over.id);
                          return arrayMove(items, oldIndex, newIndex);
                        });
                      }
                    }}
                  >
                    <SortableContext
                      items={orderedPreviewQuestions.map(q => q.id)}
                      strategy={verticalListSortingStrategy}
                    >
                      <div className="space-y-3 max-h-96 overflow-y-auto">
                        {orderedPreviewQuestions.map((question, index) => {
                          // Define SortablePreviewItem outside the map to avoid React hooks issues
                          return (
                            <SortablePreviewQuestionItem
                              key={question.id}
                              question={question}
                              index={index}
                              markingScheme={mergeTestData.markingScheme}
                            />
                          );
                        })}
                      </div>
                    </SortableContext>
                  </DndContext>
                </div>
              )}

              {/* Select Questions Section */}
              {selectedTests.length > 0 && (
                <div className="bg-gray-50 dark:bg-gray-700/50 rounded-lg p-6">
                  <div className="flex items-center justify-between mb-4">
                    <h3 className="text-lg font-semibold text-gray-900 dark:text-white">
                      Step 2: Select Questions ({selectedQuestions.size} selected)
                    </h3>
                    <div className="flex gap-2">
                      <button
                        onClick={() => {
                          // Select all questions from selected tests
                          const allQuestions = new Set();
                          selectedTests.forEach(testId => {
                            const test = tests.find(t => t.id === testId);
                            if (test && test.questions) {
                              test.questions.forEach((q, idx) => {
                                allQuestions.add(`${testId}-${idx}`);
                              });
                            }
                          });
                          setSelectedQuestions(allQuestions);
                        }}
                        className="px-3 py-1 text-xs bg-blue-600 text-white rounded-md hover:bg-blue-700"
                      >
                        Select All
                      </button>
                      <button
                        onClick={() => setSelectedQuestions(new Set())}
                        className="px-3 py-1 text-xs bg-gray-600 text-white rounded-md hover:bg-gray-700"
                      >
                        Deselect All
                      </button>
                    </div>
                  </div>
                  <div className="space-y-4 max-h-96 overflow-y-auto">
                    {selectedTests.map((testId, testMapIdx) => {
                      const test = tests.find(t => t.id === testId);
                      if (!test || !test.questions || test.questions.length === 0) {
                        return <div key={`empty-${testId}-${testMapIdx}`} style={{ display: 'none' }} />;
                      }
                      
                      return (
                        <div key={testId || `test-merge-${testMapIdx}`} className="bg-white dark:bg-gray-800 rounded-lg p-4 border border-gray-200 dark:border-gray-700">
                          <div className="font-semibold text-gray-900 dark:text-white mb-3 flex items-center justify-between">
                            <span>{test.title}</span>
                            <span className="text-sm font-normal text-gray-600 dark:text-gray-400">
                              {(test.questions || []).filter((q, idx) => selectedQuestions.has(`${testId}-${idx}`)).length} / {(test.questions || []).length} selected
                            </span>
                          </div>
                          <div className="space-y-2">
                            {(test.questions || []).map((question, qIndex) => {
                              const questionId = `${testId}-${qIndex}`;
                              const isSelected = selectedQuestions.has(questionId);
                              
                              return (
                                <label
                                  key={question?.id || questionId || `q-merge-${testId}-${qIndex}`}
                                  className={`flex items-start p-3 rounded-lg border cursor-pointer transition-colors ${
                                    isSelected
                                      ? 'bg-blue-50 dark:bg-blue-900/20 border-blue-500'
                                      : 'bg-gray-50 dark:bg-gray-700/50 border-gray-200 dark:border-gray-600 hover:border-gray-300'
                                  }`}
                                >
                                  <input
                                    type="checkbox"
                                    checked={isSelected}
                                    onChange={(e) => {
                                      const newSet = new Set(selectedQuestions);
                                      if (e.target.checked) {
                                        newSet.add(questionId);
                                      } else {
                                        newSet.delete(questionId);
                                      }
                                      setSelectedQuestions(newSet);
                                    }}
                                    className="mt-1 w-4 h-4 text-blue-600 border-gray-300 rounded focus:ring-blue-500"
                                  />
                                  <div className="ml-3 flex-1 min-w-0">
                                    <div className="flex items-center gap-2 mb-1">
                                      <span className="text-xs font-medium text-gray-600 dark:text-gray-400">
                                        Q{qIndex + 1}
                                      </span>
                                      <span className="text-xs px-2 py-0.5 bg-gray-200 dark:bg-gray-600 text-gray-700 dark:text-gray-300 rounded">
                                        {question.type || 'multiple-choice'}
                                      </span>
                                      {question.difficulty && (
                                        <span className={`text-xs px-2 py-0.5 rounded ${
                                          question.difficulty === 'Easy' ? 'bg-green-100 text-green-800' :
                                          question.difficulty === 'Medium' ? 'bg-yellow-100 text-yellow-800' :
                                          'bg-red-100 text-red-800'
                                        }`}>
                                          {question.difficulty}
                                        </span>
                                      )}
                                    </div>
                                    <div className="text-sm text-gray-700 dark:text-gray-300 line-clamp-2">
                                      <KatexRenderer content={question.question || 'No question text'} />
                                    </div>
                                  </div>
                                </label>
                              );
                            })}
                          </div>
                        </div>
                      );
                    })}
                  </div>
                </div>
              )}

              {/* Error/Success Messages */}
              {(error || success) && (
                <div className={`p-4 rounded-lg ${
                  error ? 'bg-red-50 dark:bg-red-900/20 text-red-800 dark:text-red-300' :
                  'bg-green-50 dark:bg-green-900/20 text-green-800 dark:text-green-300'
                }`}>
                  {error || success}
                </div>
              )}

              {/* Action Buttons */}
              <div className="flex justify-end gap-3 pt-4 border-t border-gray-200 dark:border-gray-700">
                <button
                  onClick={() => {
                    setShowMergeModal(false);
                    setSelectedTests([]);
                    setSelectedQuestions(new Set());
                    setOrderedPreviewQuestions([]);
                  }}
                  className="px-4 py-2 border border-gray-300 dark:border-gray-600 text-gray-700 dark:text-gray-300 rounded-md hover:bg-gray-50 dark:hover:bg-gray-700"
                >
                  Cancel
                </button>
                <button
                  onClick={async () => {
                    if (!mergeTestData.title.trim()) {
                      setError('Please enter a test title');
                      return;
                    }
                    if (!mergeTestData.duration) {
                      setError('Please enter test duration');
                      return;
                    }
                    if (selectedTests.length === 0) {
                      setError('Please select at least one test');
                      return;
                    }
                    if (selectedQuestions.size === 0) {
                      setError('Please select at least one question');
                      return;
                    }

                    try {
                      setSaving(true);
                      setError(null);
                      setSuccess(null);

                      // If a specific ID is provided, check if it already exists
                      if (mergeTestData.id) {
                        const testRef = doc(db, COLLECTIONS.TESTS, mergeTestData.id);
                        const testSnap = await getDoc(testRef);
                        
                        if (testSnap.exists()) {
                          setError(`Test with ID "${mergeTestData.id}" already exists. Please use a different ID.`);
                          setSaving(false);
                          return;
                        }
                      }

                      // Use ordered preview questions if available, otherwise collect from selected
                      const mergedQuestions = orderedPreviewQuestions.length > 0
                        ? orderedPreviewQuestions.map((question, index) => {
                            // Clean the question object - remove undefined values and mergeQuestionId
                            const cleanedQuestion = {
                              ...question,
                              id: question.id || `q-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
                              markingScheme: mergeTestData.markingScheme
                            };
                            // Remove mergeQuestionId as it's only for tracking
                            delete cleanedQuestion.mergeQuestionId;
                            // Remove any undefined values
                            Object.keys(cleanedQuestion).forEach(key => {
                              if (cleanedQuestion[key] === undefined) {
                                delete cleanedQuestion[key];
                              }
                            });
                            return cleanedQuestion;
                          })
                        : (() => {
                            // Fallback: collect in original order if preview not available
                            const questions = [];
                            selectedTests.forEach(testId => {
                              const test = tests.find(t => t.id === testId);
                              if (test && test.questions) {
                                test.questions.forEach((question, qIndex) => {
                                  const questionId = `${testId}-${qIndex}`;
                                  if (selectedQuestions.has(questionId)) {
                                    // Clean the question object - remove undefined values
                                    const cleanedQuestion = {
                                      ...question,
                                      id: `q-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
                                      markingScheme: mergeTestData.markingScheme
                                    };
                                    // Remove any undefined values
                                    Object.keys(cleanedQuestion).forEach(key => {
                                      if (cleanedQuestion[key] === undefined) {
                                        delete cleanedQuestion[key];
                                      }
                                    });
                                    questions.push(cleanedQuestion);
                                  }
                                });
                              }
                            });
                            return questions;
                          })();

                      if (mergedQuestions.length === 0) {
                        setError('No questions selected');
                        setSaving(false);
                        return;
                      }

                      // Create test data
                      const newTestData = {
                        ...mergeTestData,
                        totalQuestions: mergedQuestions.length,
                        questions: mergedQuestions
                      };

                      // Clean newTestData - remove undefined values
                      const cleanedTestData = { ...newTestData };
                      Object.keys(cleanedTestData).forEach(key => {
                        if (cleanedTestData[key] === undefined) {
                          delete cleanedTestData[key];
                        }
                      });
                      
                      // Clean questions array - remove undefined values from each question
                      if (cleanedTestData.questions && Array.isArray(cleanedTestData.questions)) {
                        cleanedTestData.questions = cleanedTestData.questions.map(q => {
                          const cleanedQ = { ...q };
                          Object.keys(cleanedQ).forEach(key => {
                            if (cleanedQ[key] === undefined) {
                              delete cleanedQ[key];
                            }
                          });
                          return cleanedQ;
                        });
                      }

                      // Save the test
                      let result;
                      if (mergeTestData.id) {
                        // Create test with specific ID
                        try {
                          const testRef = doc(db, COLLECTIONS.TESTS, mergeTestData.id);
                          await setDoc(testRef, {
                            ...cleanedTestData,
                            coachingId: (coachingId && isCoachingAdmin) ? coachingId : cleanedTestData.coachingId, // Only set for coaching admins
                            createdAt: new Date(),
                            updatedAt: new Date()
                          });
                          result = { success: true, id: mergeTestData.id };
                        } catch (error) {
                          setError(`Failed to create test with ID: ${error.message}`);
                          setSaving(false);
                          return;
                        }
                      } else {
                        // Create test with auto-generated ID
                        const testDataWithCoaching = {
                          ...cleanedTestData,
                          coachingId: (coachingId && isCoachingAdmin) ? coachingId : cleanedTestData.coachingId // Only set for coaching admins
                        };
                        result = await saveTest(testDataWithCoaching);
                      }
                      
                      if (result.success) {
                        const statusMessage = mergeTestData.status === 'published' ? 'and published' : 'as draft';
                        setSuccess(`Test "${mergeTestData.title}" created successfully ${statusMessage} with ${mergedQuestions.length} questions! (ID: ${result.id || mergeTestData.id})`);
                        loadTests();
                        setTimeout(() => {
                          setShowMergeModal(false);
                          setSelectedTests([]);
                          setSelectedQuestions(new Set());
                          setOrderedPreviewQuestions([]);
                          setMergeTestData({
                            id: '',
                            title: '',
                            description: '',
                            duration: '',
                            exam: 'CAT',
                            difficulty: 'Medium',
                            status: 'draft',
                            subject: 'General',
                            markingScheme: {
                              correct: 4,
                              incorrect: -1,
                              partial: 0
                            },
                            useDynamicSections: false,
                            sections: []
                          });
                        }, 2000);
                      } else {
                        setError(result.error || 'Failed to create test');
                      }
                    } catch (err) {
                      setError(`Failed to create test: ${err.message}`);
                    } finally {
                      setSaving(false);
                    }
                  }}
                  disabled={saving || selectedQuestions.size === 0 || !mergeTestData.title.trim() || !mergeTestData.duration}
                  className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed flex items-center"
                >
                  {saving ? (
                    <>
                      <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
                      Creating...
                    </>
                  ) : (
                    <>
                      <Save className="w-4 h-4 mr-2" />
                      Create Merged Test
                    </>
                  )}
                </button>
              </div>
            </div>
          </motion.div>
        </div>
      )}

      {/* Password Prompt Modal for Merge Test */}
      {showPasswordPrompt && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <motion.div
            initial={{ opacity: 0, scale: 0.95 }}
            animate={{ opacity: 1, scale: 1 }}
            className="bg-white dark:bg-gray-800 rounded-lg shadow-xl max-w-md w-full"
          >
            <div className="p-6">
              <div className="flex items-center justify-center mb-4">
                <div className="bg-blue-100 dark:bg-blue-900/30 rounded-full p-3">
                  <Lock className="w-8 h-8 text-blue-600 dark:text-blue-400" />
                </div>
              </div>
              <h2 className="text-2xl font-bold text-gray-900 dark:text-white text-center mb-2">
                Password Required
              </h2>
              <p className="text-sm text-gray-600 dark:text-gray-400 text-center mb-6">
                Enter password to access Merge Tests section
              </p>
              
              <div className="mb-4">
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                  Password
                </label>
                <input
                  type="password"
                  value={mergePassword}
                  onChange={(e) => setMergePassword(e.target.value)}
                  onKeyPress={(e) => {
                    if (e.key === 'Enter') {
                      if (mergePassword === MERGE_TEST_PASSWORD) {
                        setIsMergeUnlocked(true);
                        setShowPasswordPrompt(false);
                        setMergePassword('');
                        setShowMergeModal(true);
                        setSelectedTests([]);
                        setSelectedQuestions(new Set());
                        setMergeTestData({
                          id: '',
                          title: '',
                          description: '',
                          duration: '',
                          exam: 'CAT',
                          difficulty: 'Medium',
                          status: 'draft',
                          subject: 'General',
                          markingScheme: {
                            correct: 4,
                            incorrect: -1,
                            partial: 0
                          },
                          useDynamicSections: false,
                          sections: []
                        });
                      } else {
                        setError('Incorrect password. Please try again.');
                        setMergePassword('');
                      }
                    }
                  }}
                  className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 dark:bg-gray-700 dark:text-white"
                  placeholder="Enter password"
                  autoFocus
                />
              </div>

              {error && error.includes('password') && (
                <div className="mb-4 p-3 bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-md">
                  <p className="text-sm text-red-800 dark:text-red-300">{error}</p>
                </div>
              )}

              <div className="flex justify-end gap-3">
                <button
                  onClick={() => {
                    setShowPasswordPrompt(false);
                    setMergePassword('');
                  }}
                  className="px-4 py-2 border border-gray-300 dark:border-gray-600 text-gray-700 dark:text-gray-300 rounded-md hover:bg-gray-50 dark:hover:bg-gray-700"
                >
                  Cancel
                </button>
                <button
                  onClick={() => {
                    if (mergePassword === MERGE_TEST_PASSWORD) {
                      setIsMergeUnlocked(true);
                      setShowPasswordPrompt(false);
                      setMergePassword('');
                      setShowMergeModal(true);
                      setSelectedTests([]);
                      setSelectedQuestions(new Set());
                      setMergeTestData({
                        id: '',
                        title: '',
                        description: '',
                        duration: '',
                        exam: 'CAT',
                        difficulty: 'Medium',
                        status: 'draft',
                        subject: 'General',
                        markingScheme: {
                          correct: 4,
                          incorrect: -1,
                          partial: 0
                        },
                        useDynamicSections: false,
                        sections: []
                      });
                    } else {
                      setError('Incorrect password. Please try again.');
                      setMergePassword('');
                    }
                  }}
                  className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 flex items-center"
                >
                  <Unlock className="w-4 h-4 mr-2" />
                  Unlock
                </button>
              </div>
            </div>
          </motion.div>
        </div>
      )}

      {/* Dynamic Section Config Modal */}
      {showSectionConfig && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4 overflow-y-auto">
          <motion.div
            initial={{ opacity: 0, scale: 0.95 }}
            animate={{ opacity: 1, scale: 1 }}
            className="bg-white dark:bg-gray-800 rounded-lg shadow-xl max-w-4xl w-full max-h-[90vh] overflow-y-auto"
          >
            <div className="sticky top-0 bg-white dark:bg-gray-800 border-b border-gray-200 dark:border-gray-700 p-6 flex items-center justify-between z-10">
              <h2 className="text-2xl font-bold text-gray-900 dark:text-white">Configure Dynamic Sections</h2>
              <button
                onClick={() => {
                  setShowSectionConfig(false);
                  setIsSectionConfigValid(false);
                }}
                className="text-gray-400 hover:text-gray-600 dark:hover:text-gray-300"
              >
                <X className="w-6 h-6" />
              </button>
            </div>

            <div className="p-6">
              <DynamicSectionConfig
                sections={mergeTestData.sections}
                totalQuestions={selectedQuestions.size || mergeTestData.totalQuestions || 0}
                onSectionsChange={(sections) => {
                  setMergeTestData(prev => ({
                    ...prev,
                    sections: sections,
                    useDynamicSections: sections.length > 0
                  }));
                }}
                onValidationChange={(isValid) => {
                  setIsSectionConfigValid(isValid);
                }}
              />
            </div>

            <div className="sticky bottom-0 bg-white dark:bg-gray-800 border-t border-gray-200 dark:border-gray-700 p-6 flex justify-end gap-3">
              <button
                onClick={() => {
                  setShowSectionConfig(false);
                  setIsSectionConfigValid(false);
                }}
                className="px-4 py-2 border border-gray-300 dark:border-gray-600 text-gray-700 dark:text-gray-300 rounded-md hover:bg-gray-50 dark:hover:bg-gray-700"
              >
                Cancel
              </button>
              <button
                onClick={() => {
                  if (isSectionConfigValid) {
                    setShowSectionConfig(false);
                  } else {
                    setError('Please fix validation errors before saving');
                  }
                }}
                disabled={!isSectionConfigValid}
                className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                Save Sections
              </button>
            </div>
          </motion.div>
        </div>
      )}

      {/* Translation Modal */}
      {showTranslationModal && (
        <div 
          className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 overflow-y-auto" 
          style={{ zIndex: 9999, position: 'fixed' }}
          onClick={(e) => {
            if (e.target === e.currentTarget) {
              setShowTranslationModal(false);
              setTranslatedQuestion(null);
              setError(null);
              setTranslating(false);
            }
          }}
        >
          <motion.div
            initial={{ opacity: 0, scale: 0.95 }}
            animate={{ opacity: 1, scale: 1 }}
            className="bg-white dark:bg-gray-800 rounded-lg shadow-xl max-w-4xl w-full max-h-[90vh] overflow-y-auto relative"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="sticky top-0 bg-white dark:bg-gray-800 border-b border-gray-200 dark:border-gray-700 p-6 flex items-center justify-between z-10">
              <h2 className="text-2xl font-bold text-gray-900 dark:text-white flex items-center">
                <Languages className="w-6 h-6 mr-2 text-green-600" />
                Hindi Translation
              </h2>
              <button
                onClick={() => {
                  setShowTranslationModal(false);
                  setTranslatedQuestion(null);
                  setError(null);
                  setTranslating(false);
                }}
                className="text-gray-400 hover:text-gray-600 dark:hover:text-gray-300"
              >
                <X className="w-6 h-6" />
              </button>
            </div>

            <div className="p-6 space-y-6">
              {error && error.includes('translate') && (
                <div className="mb-4 p-4 bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-md">
                  <p className="text-sm text-red-800 dark:text-red-300 font-semibold">Translation Error</p>
                  <p className="text-sm text-red-800 dark:text-red-300 mt-1">{error}</p>
                  <p className="text-xs text-red-600 dark:text-red-400 mt-2">
                    Note: Translation requires an active internet connection. If the issue persists, the translation service may be temporarily unavailable.
                  </p>
                </div>
              )}
              {translating ? (
                <div className="flex items-center justify-center py-12">
                  <div className="text-center">
                    <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-green-600 mx-auto mb-4"></div>
                    <p className="text-gray-600 dark:text-gray-400">Translating to Hindi...</p>
                    <p className="text-xs text-gray-500 dark:text-gray-500 mt-2">This may take a few seconds</p>
                  </div>
                </div>
              ) : translatedQuestion && (translatedQuestion.question || translatedQuestion.options?.length > 0) ? (
                <>
                  <div className="bg-gray-50 dark:bg-gray-700/50 rounded-lg p-6">
                    <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-4 flex items-center">
                      <span className="bg-green-100 text-green-800 text-xs px-2 py-1 rounded mr-2">
                        Q{(translatedQuestion.originalIndex !== undefined && translatedQuestion.originalIndex !== null) ? translatedQuestion.originalIndex + 1 : '?'}
                      </span>
                      Question (हिंदी)
                    </h3>
                    <div className="text-gray-800 dark:text-gray-200 min-h-[50px] p-3 bg-white dark:bg-gray-800 rounded border border-gray-200 dark:border-gray-700">
                      {translatedQuestion.question && String(translatedQuestion.question).trim() ? (
                        <KatexRenderer content={String(translatedQuestion.question)} />
                      ) : (
                        <div>
                          <p className="text-gray-500 italic mb-2">Translation is being processed...</p>
                          <p className="text-xs text-gray-400">If this persists, the translation may have failed. Check console for details.</p>
                        </div>
                      )}
                    </div>
                    {translatedQuestion.questionImageUrl && !translatedQuestion.question?.includes('<img') && (
                      <div className="mt-4">
                        <img
                          src={translatedQuestion.questionImageUrl}
                          alt="Question"
                          className="max-w-full h-auto max-h-64 rounded"
                        />
                      </div>
                    )}
                  </div>

                  {(translatedQuestion.type === 'multiple-choice' || translatedQuestion.type === 'multiple-choice-multi') && 
                   translatedQuestion.options && Array.isArray(translatedQuestion.options) && translatedQuestion.options.length > 0 && (
                    <div className="bg-gray-50 dark:bg-gray-700/50 rounded-lg p-6">
                      <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-4">
                        Options (विकल्प)
                      </h3>
                      <div className="space-y-3">
                        {translatedQuestion.options.map((option, optIndex) => (
                          <div key={optIndex} className="flex items-start space-x-3 p-3 bg-white dark:bg-gray-800 rounded border border-gray-200 dark:border-gray-700">
                            <div className="flex items-center mt-1">
                              <input
                                type={translatedQuestion.type === 'multiple-choice-multi' ? "checkbox" : "radio"}
                                checked={translatedQuestion.type === 'multiple-choice-multi' 
                                  ? Array.isArray(translatedQuestion.correctAnswer) && translatedQuestion.correctAnswer.includes(optIndex)
                                  : translatedQuestion.correctAnswer === optIndex
                                }
                                readOnly
                                className="mr-2"
                              />
                              <span className="text-sm font-medium text-gray-600 dark:text-gray-400">
                                {String.fromCharCode(65 + optIndex)}.
                              </span>
                            </div>
                            <div className="flex-1">
                              <div className="text-sm text-gray-800 dark:text-gray-200 min-h-[30px]">
                                {option ? (
                                  <KatexRenderer content={option} />
                                ) : (
                                  <span className="text-gray-400 italic">No translation</span>
                                )}
                              </div>
                              {translatedQuestion.optionImageUrls && translatedQuestion.optionImageUrls[optIndex] && !option?.includes('<img') && (
                                <div className="mt-2">
                                  <img
                                    src={translatedQuestion.optionImageUrls[optIndex]}
                                    alt={`Option ${optIndex + 1}`}
                                    className="max-w-full h-auto max-h-32 rounded"
                                  />
                                </div>
                              )}
                            </div>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}

                  {translatedQuestion.explanation && (
                    <div className="bg-gray-50 dark:bg-gray-700/50 rounded-lg p-6">
                      <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-4">
                        Explanation (स्पष्टीकरण)
                      </h3>
                      <div className="text-gray-800 dark:text-gray-200">
                        <KatexRenderer content={translatedQuestion.explanation} />
                      </div>
                    </div>
                  )}

                  <div className="flex justify-end gap-3 pt-4 border-t border-gray-200 dark:border-gray-700">
                    <button
                      onClick={() => {
                        setShowTranslationModal(false);
                        setTranslatedQuestion(null);
                        setError(null);
                        setTranslating(false);
                      }}
                      className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700"
                    >
                      Close
                    </button>
                  </div>
                </>
              ) : !translating ? (
                <div className="text-center py-12">
                  <p className="text-gray-500 mb-2">No translation available</p>
                  <p className="text-xs text-gray-400">Click the translate button to start translation</p>
                </div>
              ) : null}
            </div>
          </motion.div>
        </div>
      )}

      {/* Test Evaluation Modal */}
      <TestEvaluationModal
        isOpen={evaluationModalTest !== null}
        onClose={() => setEvaluationModalTest(null)}
        test={evaluationModalTest}
      />
    </div>
  );
};

export default TestManagement;
